// Buenos Aires, "Jacarandas by the Obelisco": a warm spring night in early November on Avenida 9 de Julio, the
// jacarandas in full purple bloom. We stand on the west sidewalk of the avenue, just south of Corrientes, and face
// east across its lanes: the Obelisco (1936) rises white and floodlit from the oval lawn of the Plaza de la
// Republica in the middle of the avenue; behind it the far side of 9 de Julio, Parisian blocks with slate mansards, a
// corner dome, and to its right the mouth of Avenida Corrientes, the street of theatres, running away east under its
// marquees. A short shower has just passed: the asphalt is wet and mirrors everything, the clouds are breaking up and
// a young moon (lit on its left, as seen from the south) hangs over the river side of town.
// Back to front: sky, stars, the moon, two decks of cloud, a plane on its way down to the city airport -> far towers
// -> far blossoms -> the far blocks of 9 de Julio, Corrientes with its marquees, the rooftop sign (the billboard) ->
// the Obelisco and its plaza, small figures on the lawn -> the wet avenue (mirror of all of it, lamp streaks), its
// far lanes (colectivos and black-and-yellow taxis heading north), the median with its jacarandas, the near lanes ->
// our sidewalk: a kiosco, a bandoneon player and a tango couple on the corner with a few people watching, walkers,
// a dog walker with his pack, a parrilla's tables and its waiter, a pizza-by-the-slice counter -> the jacaranda
// over the corner -> purple blossoms coming down at three depths.
// render(t, { crowd }) thins the people (CROWD below is the default); crowd 0 draws nobody: the kiosco, the counter and
// the tables stand lit and unattended, the bandoneon's chair stays empty.
//
// The loop's timeline (seconds; every period divides 240):
//   event                                             time                         sound
//   the tango couple dances on the corner             all loop, 80 beats a minute
//   ...a slow dip, the watchers clap                  36, 108, 180, 228            chime a second in
//   a slice of pizza slid over the counter            every 24 s (6, 30, 54 ...)
//   the waiter carries a tray out to a table          every 40 s (2, 42, 82 ...)
//   colectivo on the near lanes (left to right)       14 - 26, 94 - 106, 174 - 186 horn at 15, 95, 175
//   colectivo on the far lanes (right to left)        54 - 70, 134 - 150, 214 - 230
//   someone buys at the kiosco                        20, 80, 140, 200 (10 s each)
//   the dog walker and his pack                       40 - 82 (left to right), 150 - 192 (back)
//   a plane comes down over the river                 62 - 104
//   a neighbour shakes out a cloth on a balcony       70 - 82
//   the Obelisco takes the flag's sky blue and white  112 - 160 (fades in over 6 s) chime at 114
//   the theatre on Corrientes lets out                160 - 206
//   a shooting star                                   197
//   the sign's lamp stutters                          47, 171
// The three bigger moments: the dog walker (first third), the Obelisco in sky blue (middle), the theatre letting out
// (last third). Longest gap between two starts: 17 s (180 -> 197).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, text, textWidth, reflect, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const OB = { x: 240, base: 170, top: 42, tip: 24, w0: 15, w1: 9 };   // the Obelisco: axis, foot row, shoulder row, tip, widths
const PLAZA = { y0: 164, y1: 176, x0: 150, x1: 330 };                // the oval lawn round its foot
const ROAD0 = 176, MED0 = 193, MED1 = 197, ROAD1 = 216;               // the wet avenue: far lanes, median, near lanes
const WALK0 = 218;                                                    // our sidewalk starts here (the kerb above it)
const FAR_FOOT = 160;                                                 // the far blocks' ground line
const CORR = { x0: 300, x1: 346 };                                    // the mouth of Avenida Corrientes
const BB = { x: 262, y: 98, w: 50, h: 20 };                           // the rooftop sign's face (the billboard)
const MOON = { x: 404, y: 44, r: 8 };
const KIOSCO = { x: 6, y: 234, w: 40 };                               // the kiosco booth: left edge, foot row, width
const BANDO = { x: 113, y: 240 };                                     // the bandoneon player's chair
const TANGO = { x: 160, y: 244 };                                     // the couple's spot on the corner
const PIZZA = { x: 432, y: 246 };                                     // the counter's left edge, foot row
const NEAR_TREE = { x: 92, base: 236 };                               // the jacaranda over the corner
const personH = y => Math.round(13 + (y - 174) * 0.135);              // how tall a grown-up is with feet on row y

// The share of the people the scene shows by default: 1 = everyone, 0.5 = about half, anything just above 0 = only the
// cast the night needs (the dancers and the bandoneon, the waiter, the kiosco and pizza men, the dog walker and
// whoever a timed event is about), 0 = nobody. render(t, { crowd }) overrides it for one frame.
const CROWD = 0.55;
// Everyone who can thin has a rank, given once at build time: CAST is there at every level above 0, a rank in (0, 1)
// while it is below the level, and rank 0 is for what is not a person (always drawn).
const CAST = -1;
const spread = i => frac((i + 0.5) * 0.6180339887);
const seen = (rank, crowd) => rank === 0 || (crowd > 0 && rank < crowd);

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const box = (x0, y0, bw, bh, col) => { for (let y = y0; y < y0 + bh; y++) for (let i = x0; i < x0 + bw; i++) put(i, y, col); };
  return { c, x, put, alpha, box, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function line(c, x0, y0, x1, y1, col) {               // integer Bresenham line
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  c.fillStyle = col;
  for (;;) {
    c.fillRect(x0, y0, 1, 1);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function makePuff(r, light, dark) {                   // dithered soft blob for smoke and steam
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}
const tintCache = {};
function tint(col, tcol, a) {                         // #rrggbb between two colours
  const k = col + tcol + a;
  if (!tintCache[k]) {
    const A = hex(col), B = hex(tcol);
    tintCache[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * a).toString(16).padStart(2, '0')).join('');
  }
  return tintCache[k];
}
const darker = (c, a = 0.38) => tint(c, '#05040a', a);
const pick = (ramp, v, i, y) => ramp[clamp(Math.floor(v * (ramp.length - 1) + bayer(i, y)), 0, ramp.length - 1)];
function vnoise(x, y, s) {                            // smooth value noise in [0, 1)
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi, u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(hash(xi, yi, s), hash(xi + 1, yi, s), u), lerp(hash(xi, yi + 1, s), hash(xi + 1, yi + 1, s), u), v);
}
function makePool(rx, ry, col, maxA) {                // a flattened dithered ellipse of light (drawn additively)
  const rgb = hex(col), w = rx * 2 + 1, h = ry * 2 + 1, [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const dd = Math.hypot((i - rx) / rx, (y - ry) / ry);
    if (dd >= 1) continue;
    const q = Math.floor(Math.pow(1 - dd, 1.5) * 5 + bayer(i, y)) / 5, o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = Math.round(Math.min(1, q) * maxA * 255);
  }
  x.putImageData(img, 0, 0);
  return { c, rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}

// a full-frame layer cut down to the box its pixels fill, drawn back at the same place: { c, x, y }
function trim(c) {
  const d = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
  let x0 = c.width, y0 = c.height, x1 = -1, y1 = -1;
  for (let y = 0; y < c.height; y++) for (let x = 0; x < c.width; x++) if (d[(y * c.width + x) * 4 + 3]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [o, ox] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  ox.drawImage(c, x0, y0, o.width, o.height, 0, 0, o.width, o.height);
  return { c: o, x: x0, y: y0 };
}
const put1 = L => ctx.drawImage(L.c, L.x, L.y);

/* ================= Sky, stars, the moon ================= */
const SKY = ['#040720', '#060a26', '#080e2c', '#0b1232', '#0f1638', '#141a40', '#1a1f48', '#21244f', '#292955', '#332e5a',
  '#3e345e', '#4a3a60', '#583f60'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 165), 1.4) * 0.9;
    v += Math.max(0, 1 - Math.hypot((i - 250) / 260, (y - 150) / 90)) * 0.16;   // the avenue's glow in the damp air
    return v;
  });
}
const STARS = [];
{ const r = rng(77); for (let i = 0; i < 54; i++) STARS.push({ x: Math.floor(r() * W), y: 1 + Math.floor(r() * 96), k: 18 + Math.floor(r() * 50), ph: r(), b: 0.3 + r() * 0.6 }); }
// a young waxing moon: seen from the southern hemisphere its lit side is the left one
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, d = Math.hypot(dx, dy);
    if (d > r + 0.3) continue;
    const lim = Math.sqrt(Math.max(0, r * r - dy * dy)), edge = -lim * 0.15;   // the terminator, a little left of centre
    if (dx > edge + 0.5) { p.put(i, y, d > r - 0.6 ? '#1c2244' : '#151a38'); continue; }   // earthshine
    let col = d > r - 1 ? '#e4dcc4' : '#fbf4de';
    if (vnoise(i * 0.55, y * 0.55, 91) > 0.62) col = '#d8d0ba';
    if (dx > edge - 1) col = '#b4ae9a';
    p.put(i, y, col);
  }
  return p.done();
}
const SHOOT = { t: 197, x: 150, y: 14, dx: -54, dy: 20 };
function drawShootingStar(t) {
  const u = (t - SHOOT.t) / 0.9;
  if (u < 0 || u >= 1) return;
  for (let k = 0; k < 7; k++) {
    const v = u - k * 0.035;
    if (v < 0) break;
    ctx.globalAlpha = (1 - k / 7) * Math.sin(Math.PI * u);
    px(ctx, Math.round(SHOOT.x + SHOOT.dx * v), Math.round(SHOOT.y + SHOOT.dy * v), k ? '#c8d4ff' : '#ffffff');
  }
  ctx.globalAlpha = 1;
}

/* ================= Clouds (tileable): the tail of the shower, breaking up ================= */
const CLOUD_DEFS = [
  { seed: 11, y: 4, h: 44, banks: 3, k: 1, sx: 1.6, pal: { top: '#3a4472', body: '#1a2046', core: '#151a3c', bot: '#2c2c52', bot2: '#22244a' } },
  { seed: 29, y: 46, h: 46, banks: 3, k: 2, sx: 1.4, pal: { top: '#4a5282', body: '#222a54', core: '#1c2248', bot: '#54426a', bot2: '#3a345e' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 60 + r() * 100, base = h * (0.45 + r() * 0.35);
    const np = 5 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = (7 + r() * 12) * def.sx, ry = 3 + r() * 5.5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.4;
      if (v > best) best = v;
    }
    best += 0.14 * Math.sin(TAU * 9 * x / W + y * 0.37 + def.seed) * Math.sin(TAU * 4 * x / W - y * 0.21);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = painter(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.put(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}

/* ================= A plane coming down to the city airport, by the river ================= */
const PLANE = { a: 62, b: 104, x0: -10, x1: 300, y0: 30, y1: 64 };
function drawPlane(t) {
  if (t < PLANE.a || t >= PLANE.b) return;
  const u = (t - PLANE.a) / (PLANE.b - PLANE.a), x = Math.round(lerp(PLANE.x0, PLANE.x1, u)), y = Math.round(lerp(PLANE.y0, PLANE.y1, u));
  const fade = Math.min(1, u * 8) * Math.min(1, (1 - u) * 5);
  ctx.globalAlpha = fade;
  rect(ctx, x - 3, y, 7, 1, '#2a2c44'); px(ctx, x - 3, y - 1, '#2a2c44');
  for (let k = 0; k < 3; k++) px(ctx, x - 1 + k * 2, y, '#ffe6a8');                 // cabin windows
  px(ctx, x + 4, y, '#fffaf0'); halo(HL.landing, x + 6, y + 1, 0.55 * fade);        // the landing light
  if (frac(t * 0.8) < 0.18) { px(ctx, x - 3, y - 1, '#ff4a4a'); halo(HL.red, x - 3, y - 1, 0.6 * fade); }
  if (frac(t * 0.8 + 0.5) < 0.1) px(ctx, x, y + 1, '#ffffff');
  ctx.globalAlpha = 1;
}

/* ================= Far towers beyond the avenue (the city goes on to the river) ================= */
const FAR_LIGHTS = [];                                // far windows that twinkle: { x, y, k, ph }
const BEACONS = [];                                   // red aircraft lamps on the towers: { x, y, ph }
function renderFar() {
  const p = painter(W, H), r = rng(23);
  const towers = [[4, 30, 70], [40, 22, 92], [96, 26, 58], [128, 18, 84], [176, 24, 76], [212, 16, 96], [270, 22, 88],
    [300, 30, 66], [338, 20, 80], [362, 26, 56], [396, 18, 74], [430, 30, 90], [462, 22, 68]];
  for (const [x0, w, top] of towers) {
    for (let i = x0; i < x0 + w; i++) for (let y = top; y < FAR_FOOT; y++) {
      const edge = i === x0 ? 0.2 : i === x0 + w - 1 ? -0.15 : 0;
      p.put(i, y, pick(['#0e1230', '#141838', '#1a1e42', '#22264c'], 0.35 + edge + (y - top) / 240 + (y === top ? 0.4 : 0), i, y));
    }
    for (let y = top + 3; y < FAR_FOOT - 2; y += 3) for (let i = x0 + 2; i < x0 + w - 2; i += 3) {
      const v = r();
      if (v < 0.16) p.put(i, y, v < 0.05 ? '#a8b4e0' : '#e8b870');
      else if (v < 0.2) FAR_LIGHTS.push({ x: i, y, k: 20 + Math.floor(r() * 60), ph: r() });
    }
    if (top < 80) BEACONS.push({ x: x0 + (w >> 1), y: top - 2, ph: r() });
    for (let y = top - 2; y < top; y++) p.put(x0 + (w >> 1), y, '#22264c');
  }
  return p.done();
}

/* ================= The far side of 9 de Julio: Parisian blocks, slate mansards, two domes ================= */
const STONE = ['#221e30', '#2e2838', '#3c3442', '#4c414c', '#5e5056', '#726260', '#8a766c', '#a48c7a'];
const SLATE = ['#12142a', '#1a1c34', '#24263e', '#30324a', '#3e4058'];
const COPPER = ['#14262a', '#1e3836', '#2c4c46', '#3e6458', '#5a806a'];
const BLOCKS = [
  { x0: -2, x1: 64, top: 80, floors: 7, dome: { x: 30, r: 15, top: 50, pal: COPPER } },
  { x0: 64, x1: 150, top: 96, floors: 5 },
  { x0: 150, x1: 206, top: 106, floors: 4 },
  { x0: 206, x1: 262, top: 116, floors: 3 },
  { x0: 262, x1: CORR.x0, top: 124, floors: 2 },
  { x0: CORR.x1, x1: 424, top: 92, floors: 5, theatre: true },
  { x0: 424, x1: 482, top: 84, floors: 6, dome: { x: 440, r: 11, top: 62, pal: SLATE } },
];
const WIN_TOG = [];                                   // windows that switch on and off: { id, x, y, n, off }
const FIGWIN = [];                                    // lit windows someone walks past: { x, y, k, ph }
const TV_WIN = [];                                    // a television's blue flicker: { x, y }
const BALC = { x: 108, y: 0 };                        // the balcony where a neighbour shakes out a cloth (y set below)
const SHOPS = [];                                     // lit shop fronts on the ground floors: { x, w, col }
function drawBlock(p, B, r) {
  const put = p.put, mans = 9, body = B.top + mans + 2;
  // the mansard: slate, a row of dormers, a cornice under it
  for (let i = B.x0; i < B.x1; i++) for (let y = B.top; y < body; y++) {
    const k = y - B.top, inset = Math.max(0, 2 - k);
    if (i < B.x0 + inset || i >= B.x1 - inset) continue;
    put(i, y, k === 0 ? SLATE[4] : y >= body - 2 ? STONE[5 - (y - body + 2)] : pick(SLATE, 0.55 - k / 18 + ((i & 3) === 0 ? -0.15 : 0), i, y));
  }
  for (let i = B.x0 + 4; i < B.x1 - 5; i += 9) {        // dormers, a few lit
    const lit = r() < 0.35;
    for (let y = B.top + 2; y < B.top + 7; y++) for (let j = 0; j < 4; j++) put(i + j, y, y === B.top + 2 ? SLATE[4] : j === 0 || j === 3 ? STONE[3] : lit ? (y < B.top + 4 ? '#ffe2a4' : '#f2b462') : '#0e1024');
  }
  // the facade: stone warmed from the street below, a string course per floor, tall French windows with iron balconies
  for (let i = B.x0; i < B.x1; i++) for (let y = body; y < FAR_FOOT; y++) {
    let v = 0.3 + 0.45 * Math.pow((y - body) / (FAR_FOOT - body), 1.3) + (hash(i, y, 51) - 0.5) * 0.06;
    if (i === B.x0) v += 0.15; else if (i === B.x1 - 1) v -= 0.2;
    put(i, y, pick(STONE, v, i, y));
  }
  const gf = FAR_FOOT - 18, fh = (gf - body) / B.floors;
  for (let f = 0; f < B.floors; f++) {
    const fy = Math.round(body + f * fh), wh = Math.max(4, Math.round(fh) - 4);
    for (let i = B.x0; i < B.x1; i++) put(i, fy + Math.round(fh) - 1, STONE[6]);
    for (let wx = B.x0 + 4; wx + 3 <= B.x1 - 3; wx += 7) {
      const v = r(), lit = v < 0.34;
      for (let j = 0; j < wh; j++) for (let i = 0; i < 3; i++) put(wx + i, fy + 2 + j, lit ? (j < 2 ? '#ffe6ac' : '#f4b866') : (j < 2 && i === 0 ? '#22264a' : '#10122a'));
      for (let i = -1; i <= 3; i++) put(wx + i, fy + 1, STONE[6]);                                     // lintel
      if (f > 0 && (f & 1) === (B.x0 & 1)) for (let i = -1; i <= 3; i++) {                              // iron balcony
        put(wx + i, fy + wh, '#14121c'); if (((i + 1) & 1) === 0) put(wx + i, fy + wh + 1, '#14121c');
      }
      if (lit && v < 0.12) FIGWIN.push({ x: wx, y: fy + 3, h: wh - 2, k: 3 + Math.floor(r() * 5), ph: r() });
      else if (!lit && v < 0.6) WIN_TOG.push({ id: WIN_TOG.length, x: wx, y: fy + 2, h: wh, n: 3 + Math.floor(r() * 4), off: r() });
      else if (!lit && v < 0.66) TV_WIN.push({ x: wx, y: fy + 3, h: wh - 2 });
    }
  }
  // the ground floor: shop fronts and a cornice
  for (let i = B.x0; i < B.x1; i++) { put(i, gf - 1, STONE[7]); put(i, gf, STONE[3]); }
  if (!B.theatre) for (let sx = B.x0 + 3; sx + 10 < B.x1; sx += 16) {
    const lit = r() < 0.7, col = of(r, ['#ffd890', '#ffe6b8', '#ffc070', '#c8e0ff']);
    for (let y = gf + 4; y < FAR_FOOT; y++) for (let i = 0; i < 11; i++) put(sx + i, y, lit ? (y === gf + 4 ? '#fff4d8' : y > FAR_FOOT - 3 ? tint(col, '#3a2a30', 0.5) : col) : '#141224');
    if (lit) { SHOPS.push({ x: sx + 5, y: gf + 9, col }); for (let y = gf + 6; y < FAR_FOOT - 2; y += 4) for (let i = 1; i < 10; i += 3) put(sx + i, y, '#8a5a3a'); }
    for (let i = -1; i < 12; i++) put(sx + i, gf + 2, of(r, ['#7a2a2a', '#2a4a3a', '#2a3a5a', '#5a3a2a']));   // awnings
  }
  if (B.dome) drawDome(p, B.dome);
}
function drawDome(p, D) {
  const put = p.put, base = D.top + D.r + 6;
  for (let y = base; y < base + 6; y++) for (let i = -D.r - 1; i <= D.r + 1; i++) put(D.x + i, y, y === base ? STONE[6] : pick(STONE, 0.5 + (i < 0 ? 0.1 : -0.1), D.x + i, y));   // the drum
  for (let i = -D.r + 3; i <= D.r - 3; i += 4) for (let y = base + 1; y < base + 5; y++) put(D.x + i, y, '#10122a');
  for (let y = D.top + 6; y < base; y++) {
    const dy = (base - y) / (base - D.top - 6), hw = Math.round(D.r * Math.sqrt(Math.max(0, 1 - dy * dy * dy)));
    for (let i = -hw; i <= hw; i++) {
      const v = 0.55 - (i / (D.r + 1)) * 0.4 + (1 - dy) * 0.1 + ((i + 40) % 4 === 0 ? -0.2 : 0);
      put(D.x + i, y, pick(D.pal, v, D.x + i, y));
    }
  }
  for (let y = D.top; y < D.top + 6; y++) for (let i = -1; i <= 1; i++) put(D.x + i, y, y === D.top ? STONE[7] : i === 0 && y > D.top + 1 ? '#ffd890' : STONE[4]);   // the lantern
  put(D.x, D.top - 1, STONE[6]); put(D.x, D.top - 2, STONE[5]);
}
// Avenida Corrientes running away east between two corner blocks: its walls in perspective, a marquee on each,
// the street floor glowing, the far end lost in light
const CORR_VP = { x: 323, y: 138 };
const CORR_MARQ = [];                                 // receding marquee bulbs: { x, y, ph }
function drawCorrientes(p, r) {
  const put = p.put, { x0, x1 } = CORR, vp = CORR_VP;
  for (let i = x0; i < x1; i++) {
    const left = i < vp.x, z = left ? (i - x0) / (vp.x - x0) : (x1 - 1 - i) / (x1 - 1 - vp.x);   // 0 at the corners, 1 far off
    const zz = Math.pow(z, 0.8), cornerTop = left ? 112 : 96;
    const top = Math.round(lerp(cornerTop, vp.y - 12, zz)), foot = Math.round(lerp(164, vp.y + 2, zz)), hgt = foot - top;
    for (let y = top; y < foot; y++) {
      const f = (foot - y) / hgt;                       // 0 at the pavement, 1 at the cornice
      let v = 0.18 + 0.38 * (1 - f) + (left ? 0.12 : -0.02) - zz * 0.12;
      const row = f * 9, wr = row - Math.floor(row);
      if (f > 0.3 && f < 0.92 && wr > 0.35 && wr < 0.8 && Math.floor(i * (1.6 - zz) + (left ? 0 : 1)) % 3 !== 0) {   // windows
        put(i, y, hash(i >> 1, Math.floor(row), left ? 63 : 64) < 0.45 ? (wr < 0.5 ? '#ffe2a4' : '#f2b462') : '#12142c'); continue;
      }
      if (y === top) v += 0.25;
      put(i, y, pick(STONE, v, i, y));
    }
    // the marquees along both sides, smaller as they recede, and the glow under them
    const my = Math.round(foot - hgt * 0.24), mh = Math.max(1, Math.round(lerp(4, 1, zz)));
    const seg = Math.floor((i - x0) / 5), lit = zz < 0.6 && hash(seg, left ? 1 : 2, 65) < 0.75;    // a marquee here and there, the rest shop fronts
    if (lit) for (let y = my; y < my + mh; y++) put(i, y, y === my ? '#fff4c8' : ['#ffd36a', '#ff8a5a', '#f4f0e0', '#8ac8ff'][seg % 4]);
    for (let y = my + mh; y < foot; y++) put(i, y, pick(['#1e1828', '#2a2030', '#3a2a30', '#5a4040'], 0.55 - (y - my - mh) / Math.max(2, foot - my - mh) * 0.3 - zz * 0.35 + ((i >> 1) % 3 === 0 ? 0.15 : 0), i, y));
    if ((i & 1) === 0 && lit) CORR_MARQ.push({ x: i, y: my - 1, ph: r() });
    // the street between the walls: wet asphalt, a lane line, the marquees' light lying on it
    for (let y = foot; y < 164; y++) {
      const d = (y - vp.y) / (164 - vp.y), mid = Math.abs(i - vp.x) / (1 + d * 24);
      let col = pick(['#100e1a', '#16141e', '#1e1a26', '#2a2230', '#4a3634'], 0.15 + (1 - d) * 0.35 + Math.max(0, 0.3 - mid) * 0.3, i, y);
      if (mid < 0.08 && (y & 3) < 2) col = '#6a6070';
      put(i, y, col);
    }
  }
  for (let y = vp.y - 3; y < vp.y + 2; y++) for (let i = -1; i <= 1; i++) put(vp.x + i, y, y >= vp.y ? '#ffe6a8' : '#a8806a');   // the far end, where the lights run together
}
// The theatre on the corner of Corrientes: a marquee with bulbs round it and the show's name, a blade sign up the front
const MARQ = { x: 352, y: 132, w: 64, h: 15 };
const BLADE = { x: 406, y: 92, w: 9, h: 38 };
const MARQ_BULBS = [];                                // { x, y, k } in chase order
function drawTheatre(p) {
  const put = p.put, { x, y, w, h } = MARQ;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) put(x + i, y + j, j === 0 || j === h - 1 || i === 0 || i === w - 1 ? '#3a2a1a' : j < 2 ? '#2a1a3a' : '#140e22');
  for (let i = 0; i < w; i += 2) { MARQ_BULBS.push({ x: x + i, y }); }
  for (let j = 2; j < h; j += 2) MARQ_BULBS.push({ x: x + w - 1, y: y + j });
  for (let i = w - 1; i >= 0; i -= 2) MARQ_BULBS.push({ x: x + i, y: y + h - 1 });
  for (let j = h - 3; j > 0; j -= 2) MARQ_BULBS.push({ x, y: y + j });
  MARQ_BULBS.forEach((b, k) => { b.k = k; });
  for (let j = 0; j < BLADE.h; j++) for (let i = 0; i < BLADE.w; i++) put(BLADE.x + i, BLADE.y + j, i === 0 || i === BLADE.w - 1 || j === 0 || j === BLADE.h - 1 ? '#5a1a22' : '#2a0e18');
  for (let j = BLADE.y + BLADE.h; j < MARQ.y; j++) put(BLADE.x + 4, j, '#2a2230');
  // the doors under the marquee, warm light inside
  for (let k = 0; k < 3; k++) for (let j = MARQ.y + h + 1; j < FAR_FOOT; j++) for (let i = 0; i < 9; i++) put(x + 6 + k * 19 + i, j, i === 4 ? '#8a6a4a' : j < MARQ.y + h + 3 ? '#fff0c8' : '#ffd890');
}
function renderCity() {
  const p = painter(W, H), r = rng(909);
  for (const B of BLOCKS) drawBlock(p, B, r);
  drawCorrientes(p, r);
  drawTheatre(p);
  // no window of the theatre's front may light up through its marquee or its blade sign
  const under = w => [MARQ, BLADE].some(R => w.x + 3 > R.x - 1 && w.x < R.x + R.w + 1 && w.y + (w.h || 4) > R.y - 1 && w.y < R.y + R.h + 1);
  for (const list of [WIN_TOG, FIGWIN, TV_WIN]) for (let k = list.length - 1; k >= 0; k--) if (under(list[k])) list.splice(k, 1);
  // the far sidewalk under the blocks, shops' light on it
  for (let i = 0; i < W; i++) for (let y = FAR_FOOT; y < 164; y++) p.put(i, y, pick(['#2a2432', '#3a3038', '#4e3e40', '#64504a'], 0.45 + (y - FAR_FOOT) * 0.05 + (hash(i >> 3, 0, 71) - 0.5) * 0.3, i, y));
  for (let i = 0; i < W; i++) {                        // the far half of the avenue, mostly behind the plaza
    p.put(i, 164, '#5a5260');
    for (let y = 165; y < ROAD0; y++) p.put(i, y, pick(['#0e0e1a', '#141420', '#1c1a28', '#262232'], 0.3 + (y - 165) * 0.02 + (hash(i >> 1, y, 72) - 0.5) * 0.3, i, y));
  }
  BALC.y = Math.round(BLOCKS[1].top + 11 + (FAR_FOOT - 18 - BLOCKS[1].top - 11) / 5 * 2) + 1;
  const c = p.done(), x = p.x;
  // the marquee's two lines (the house, the show), the blade sign's letters, a few shop names
  const line1 = 'TEATRO ALBA', line2 = 'NOCHE DE LUNA';
  text(x, line1, MARQ.x + ((MARQ.w - textWidth(line1)) >> 1), MARQ.y + 2, '#ffd36a');
  text(x, line2, MARQ.x + ((MARQ.w - textWidth(line2)) >> 1), MARQ.y + 8, '#f6f0ff');
  'TEATRO'.split('').forEach((ch, k) => text(x, ch, BLADE.x + 3, BLADE.y + 2 + k * 6, '#ff8a7a', 0));
  // CAFE sits between the corner jacaranda's trunk (x 88-92) and LIBROS, clear of the median lamp's mast (x 74)
  for (const [s, sx, col] of [['CAFE', 97, '#ffe6b8'], ['LIBROS', 123, '#ffd890'], ['HELADOS', 160, '#c8e0ff'], ['FARMACIA', 20, '#9affb0']]) {
    rect(x, sx - 1, FAR_FOOT - 17, textWidth(s) + 2, 7, '#140e1c'); text(x, s, sx, FAR_FOOT - 16, col);
  }
  return c;
}

/* ================= The Obelisco: white, four-sided, tapering to a pyramid; floodlit from the plaza ================= */
// Seen a little off its corner: the left face takes the floodlights, the right one is a shade cooler. A small window
// near the top of each face, the plinth with its inscription panels, a red aircraft lamp on the tip.
const obHW = y => y >= OB.top ? lerp(OB.w0 / 2, OB.w1 / 2, (OB.base - y) / (OB.base - OB.top)) : (OB.w1 / 2) * (y - OB.tip) / (OB.top - OB.tip);
const OB_WHITE = ['#5a5c7a', '#7c7e98', '#a2a2b4', '#c4c2cc', '#dedad8', '#f2eee6', '#fffcf4'];
// mode: 0 = lit from the left, 1 = from the right, 2 = the flag's colours (sky blue, white, sky blue)
function renderObelisco(mode) {
  const p = painter(W, H), put = p.put, edge = OB.x - 1;
  const CELESTE = ['#2a4a7a', '#3e6aa0', '#5a8cc4', '#78acdf', '#9ac6ee', '#c4e0fa'];
  for (let y = OB.tip; y <= OB.base; y++) {
    const hw = obHW(y), a = Math.round(OB.x - hw), b = Math.round(OB.x + hw), h = (OB.base - y) / (OB.base - OB.tip);
    for (let i = a; i <= b; i++) {
      const left = i <= edge, u = (i - a) / Math.max(1, b - a);
      let v = 0.78 - h * 0.28 + Math.max(0, 0.18 - h) * 0.9;        // the floodlights are strongest at the foot
      if (y < OB.top) v = 0.6 + (OB.top - y) / (OB.top - OB.tip) * 0.12;   // the pyramid catches the up-lights
      if (mode === 0) v += left ? 0.12 - u * 0.1 : -0.18 - (u - 0.5) * 0.2;
      else v += left ? -0.12 + u * 0.08 : 0.08 - (u - 0.5) * 0.1;
      if (i === a || i === b) v -= 0.2;
      if (i === edge + 1 && mode !== 0) v += 0.1;
      if ((y - OB.tip) % 11 === 0 && y > OB.top) v -= 0.05;           // courses of stone, barely there
      let col = pick(OB_WHITE, v, i, y);
      if (mode === 2 && y >= OB.top) {
        const band = h < 0.36 ? 0 : h < 0.68 ? 1 : 0;                  // celeste below, white in the middle, celeste above
        col = band ? pick(OB_WHITE, v + 0.08, i, y) : pick(CELESTE, v + 0.05, i, y);
      } else if (mode === 2) col = pick(CELESTE, v, i, y);
      put(i, y, col);
    }
  }
  if (mode === 2) {                                   // the sun of May in the middle band
    const sy = Math.round(lerp(OB.base, OB.tip, 0.52));
    for (const [dx, dy, c] of [[0, 0, '#ffd23a'], [-1, 0, '#f0b030'], [1, 0, '#f0b030'], [0, -1, '#f0b030'], [0, 1, '#f0b030'], [-2, -2, '#e8a028'], [2, 2, '#e8a028'], [2, -2, '#e8a028'], [-2, 2, '#e8a028']]) put(OB.x - 1 + dx, sy + dy, c);
  }
  // the little windows at the top, one on each face
  for (const wx of [OB.x - 3, OB.x + 1]) { put(wx, OB.top + 4, '#2a2a40'); put(wx + 1, OB.top + 4, '#2a2a40'); put(wx, OB.top + 5, '#3a3a52'); put(wx + 1, OB.top + 5, '#3a3a52'); }
  // the plinth and the inscription panels
  for (let y = OB.base - 2; y <= OB.base + 1; y++) for (let i = OB.x - 10; i <= OB.x + 10; i++) put(i, y, y === OB.base - 2 ? '#f4f0e6' : pick(OB_WHITE, 0.55 - (i - OB.x) * 0.02, i, y));
  for (const [a, b] of [[OB.x - 6, OB.x - 3], [OB.x + 1, OB.x + 4]]) for (let y = OB.base - 15; y < OB.base - 7; y++) for (let i = a; i <= b; i++) put(i, y, y === OB.base - 15 ? '#8a8aa0' : '#a8a8bc');
  return p.done();
}

/* ================= The Plaza de la Republica: the oval lawn, paths, little jacarandas, lamp posts ================= */
const PL_TREES = [{ x: 168, base: 170, r: 11, seed: 4 }, { x: 314, base: 170, r: 10, seed: 6 }];
const MED_TREES = [{ x: 26, base: MED1, r: 13, seed: 8 }, { x: 452, base: MED1, r: 14, seed: 9 }];
const AVE_LAMPS = [{ x: 74, y: 120 }, { x: 190, y: 128 }, { x: 432, y: 122 }];   // tall lamps on the median: head row
const JAC = ['#1e1230', '#2e1a46', '#45265e', '#5e347a', '#7a4898', '#9866b8', '#b88ad4', '#d4b0ec'];
const JAC_SPARK = [];                                 // blossom pixels that catch the light now and then: { x, y, k, ph }
function crown(p, cx, cy, rx, ry, seed, light, spark) {   // a jacaranda's crown: purple clouds of bloom, a few green leaves
  const r = rng(seed);
  const blobs = [[0, 0, 1]];
  for (let k = 0; k < 6; k++) blobs.push([(r() - 0.5) * rx * 1.3, (r() - 0.6) * ry, 0.5 + r() * 0.4]);
  for (const [bx, by, s] of blobs) {
    const brx = rx * s, bry = ry * s;
    for (let y = -bry; y <= bry; y++) for (let i = -brx; i <= brx; i++) {
      const x = Math.round(cx + bx + i), yy = Math.round(cy + by + y);
      if (Math.hypot(i / brx, y / bry) > 1 - hash(x, yy, seed) * 0.4) continue;
      let v = light + (-y / bry) * 0.22 - (i / brx) * 0.08 + (vnoise(x / 2.5, yy / 2.5, seed) - 0.5) * 0.6;
      p.put(x, yy, hash(x, yy, seed + 1) < 0.05 ? '#2a3a2a' : pick(JAC, v, x, yy));
      if (spark && v > light + 0.15 && hash(x, yy, seed + 2) < 0.09) JAC_SPARK.push({ x, y: yy, k: 20 + Math.floor(hash(x, yy, 5) * 50), ph: hash(x, yy, 6) });
    }
  }
}
function trunk(p, x, base, top, w) {
  for (let y = top; y <= base; y++) {
    const xo = Math.round(Math.sin(y * 0.15) * 0.8);
    for (let i = 0; i < w; i++) p.put(x + xo + i - (w >> 1), y, i === 0 ? '#3a2e34' : i === w - 1 ? '#120c14' : '#241a22');
  }
}
function renderPlaza() {
  const p = painter(W, H), put = p.put;
  const cx = OB.x, cy = OB.base, rx = (PLAZA.x1 - PLAZA.x0) / 2, ry = (PLAZA.y1 - PLAZA.y0) / 2;
  for (let y = PLAZA.y0 - 1; y <= ROAD0; y++) for (let i = PLAZA.x0 - 12; i <= PLAZA.x1 + 12; i++) {
    const d = Math.hypot((i - cx) / rx, (y - cy) / ry);
    if (d > 1.12) continue;
    if (d > 1) { put(i, y, d > 1.08 ? '#6a6070' : '#8a8090'); continue; }            // the kerb round the oval
    const near = Math.hypot((i - cx) / 22, (y - cy) / 5);
    let col;
    if (near < 1) col = pick(['#4a4458', '#625a6a', '#7e7480', '#9a8e90'], 0.6 - near * 0.4 + (y - cy) * 0.03, i, y);   // the paved ring at its foot
    else col = pick(['#0c1a14', '#12241a', '#1a3022', '#24402a', '#30502e'], 0.3 + (1 - d) * 0.4 + Math.max(0, 0.5 - near * 0.2) + (hash(i, y, 81) - 0.5) * 0.2, i, y);
    if (Math.abs(y - cy) < 1 && near >= 1 && d < 0.96) col = '#5a5262';                 // a path across the lawn
    put(i, y, col);
  }
  for (const T of PL_TREES) { trunk(p, T.x, T.base, T.base - 12, 2); crown(p, T.x, T.base - 16, T.r, T.r * 0.7, T.seed, 0.55, true); }
  return p.done();
}

/* ================= The avenue: wet asphalt, lane lines, a zebra, the median; our sidewalk ================= */
const ASPH = ['#0c0c18', '#12121e', '#181826', '#20202e', '#2a2836'];
function renderRoad() {
  const p = painter(W, H), put = p.put;
  for (let y = ROAD0; y < WALK0; y++) for (let i = 0; i < W; i++) {
    const v = 0.35 + (hash(i >> 1, y, 91) - 0.5) * 0.25 + (y > MED1 ? 0.06 : 0);
    put(i, y, pick(ASPH, v, i, y));
  }
  for (const ly of [184, 206]) for (let i = 0; i < W; i++) if ((i + (ly >> 1)) % 22 < 9) put(i, ly, '#5a5866');   // lane lines
  for (let y = ROAD0; y < MED0; y++) for (let i = 384; i < 412; i++) if (((i - 384) >> 1) % 3 === 0) put(i, y, '#6a6874');   // the zebra
  for (let y = MED1 + 1; y < ROAD1; y++) for (let i = 380 + Math.round((y - MED1) * 0.3); i < 412 + Math.round((y - MED1) * 0.3); i++) if (((i - 380 - Math.round((y - MED1) * 0.3)) >> 1) % 3 === 0) put(i, y, '#7a7884');
  return p.done();
}
function renderMedian() {
  const p = painter(W, H), put = p.put;
  for (let i = 0; i < W; i++) {                        // the median: kerbs and a strip of grass and hedge
    put(i, MED0, '#5a5664'); put(i, MED1, '#3a3644');
    for (let y = MED0 + 1; y < MED1; y++) put(i, y, pick(['#0e1a12', '#162a1a', '#1e3a22', '#2a4a2a'], 0.3 + (hash(i >> 1, y, 93) - 0.4) * 0.6 + (y === MED0 + 1 ? 0.3 : 0), i, y));
    if (hash(i, 7, 94) < 0.12) put(i, MED0 + 1, pick(JAC, 0.6, i, 0));                    // fallen blossoms in the grass
  }
  for (const T of MED_TREES) { trunk(p, T.x, T.base, T.base - 20, 3); crown(p, T.x, T.base - 26, T.r, T.r * 0.75, T.seed, 0.5, true); }
  return p.done();
}
const TILE = ['#26222c', '#302a34', '#3a333c', '#463d44', '#54484c', '#62544f'];
function renderWalk() {
  const p = painter(W, H), put = p.put;
  for (let i = 0; i < W; i++) { put(i, ROAD1, '#4a4652'); put(i, ROAD1 + 1, '#8a8490'); put(i, WALK0 - 0, '#6a6470'); }   // the kerb
  for (let y = WALK0 + 1; y < H; y++) for (let i = 0; i < W; i++) {
    const tw = 8 + Math.floor((y - WALK0) / 18) * 2, row = Math.floor((y - WALK0) / (tw * 0.5));
    const seam = (i + (row & 1) * (tw >> 1)) % tw === 0 || (y - WALK0) % Math.round(tw * 0.5) === 0;
    let v = 0.42 + (y - WALK0) / 160 + (hash(Math.floor(i / tw), row, 95) - 0.5) * 0.2 + (seam ? -0.18 : 0);
    if (hash(Math.floor(i / tw), row, 96) < 0.06) v -= 0.12;          // a loose tile
    put(i, y, pick(TILE, v, i, y));
  }
  // the tree pit of the corner jacaranda
  for (let y = NEAR_TREE.base - 3; y <= NEAR_TREE.base + 2; y++) for (let i = NEAR_TREE.x - 9; i <= NEAR_TREE.x + 9; i++) put(i, y, y === NEAR_TREE.base - 3 || Math.abs(i - NEAR_TREE.x) === 9 ? '#5a5258' : '#1c1418');
  return p.done();
}

/* ================= Traffic: colectivos, black-and-yellow taxis, cars ================= */
// four lanes: two far ones heading north (right to left), two near ones heading south; y is the wheels' row
const LANES = [
  { y: 183, s: 1.3, dir: -1, n: 9, slots: 2, seed: 1 },
  { y: 191, s: 1.45, dir: -1, n: 11, slots: 2, seed: 2 },
  { y: 205, s: 1.62, dir: 1, n: 10, slots: 2, seed: 3 },
  { y: 214, s: 1.78, dir: 1, n: 8, slots: 2, seed: 4 },
];
const CAR_COLS = [['#6a6a78', '#3a3a48'], ['#8a2a30', '#4a1a20'], ['#2a4a80', '#16264a'], ['#c8c8cc', '#7a7a84'],
  ['#2a2a34', '#16161e'], ['#3a5a4a', '#1e3228'], ['#9a8a6a', '#5a4e3a']];
// colectivos: lower body, upper body, stripe, the line's number
const BUS_LIVERY = [['#c8303a', '#f0e6c8', '#2a2a3a', '580'], ['#2a8a4a', '#f0c83a', '#c8303a', '612'], ['#2a5ab0', '#eef0f4', '#e8b030', '703'],
  ['#e0782a', '#5a3a2a', '#f4e8c8', '431']];
// every sprite faces right; lamps are [x, y] inside it
function makeCar(s, body, dark, taxi) {
  const L = Math.round(20 * s), Hh = Math.round(8 * s), [c, x] = makeCanvas(L, Hh);
  const cab = Math.round(Hh * 0.45), c0 = Math.round(L * 0.26), c1 = Math.round(L * 0.72), roof = taxi ? '#f0c020' : body;
  rect(x, c0 + 1, 0, c1 - c0 - 2, 1, roof);                                           // roof
  rect(x, c0, 1, c1 - c0, cab - 1, '#1c2234'); px(x, c1 - 1, 1, '#4a5a7a');            // glass
  rect(x, Math.round((c0 + c1) / 2), 1, 1, cab - 1, taxi ? '#f0c020' : body);           // pillar
  if (taxi) { rect(x, c0, 0, 1, cab, '#f0c020'); rect(x, c1 - 1, 0, 1, cab, '#f0c020'); }
  rect(x, 0, cab, L, Hh - cab - 1, taxi ? '#14141c' : body);
  rect(x, 1, cab, L - 2, 1, taxi ? '#3a3a48' : dark === '#7a7a84' ? '#e8e8ee' : tint(body, '#ffffff', 0.25));
  rect(x, 0, Hh - 2, L, 1, taxi ? '#0a0a10' : dark);
  if (taxi) { rect(x, c0 - 1, cab + 1, c1 - c0 + 2, 1, '#f0c020'); px(x, c1 - 2, 1, '#ff3a3a'); }   // yellow band; LIBRE in the screen
  const ww = Math.max(3, Math.round(L * 0.15));
  rect(x, Math.round(L * 0.12), Hh - 2, ww, 2, '#050408'); rect(x, Math.round(L * 0.7), Hh - 2, ww, 2, '#050408');
  px(x, Math.round(L * 0.12) + 1, Hh - 2, '#5a5a66'); px(x, Math.round(L * 0.7) + 1, Hh - 2, '#5a5a66');
  px(x, L - 1, cab, '#fff6d8'); px(x, 0, cab, '#ff3a3a');
  return { c, w: L, h: Hh, front: [L - 1, cab], back: [0, cab], taxi };
}
function makeColectivo(s, [lo, up, stripe, num]) {     // a city bus: front engine, painted in its line's colours
  const L = Math.round(46 * s), Hh = Math.round(16.5 * s), [c, x] = makeCanvas(L, Hh);
  const wy = 7, wb = Math.round(Hh * 0.34), belt = wy + wb + 1;
  rect(x, 1, 0, L - 3, 1, tint(up, '#000000', 0.25));
  rect(x, 0, 1, L - 1, belt, up); rect(x, 0, belt, L - 1, Hh - belt - 2, lo);
  rect(x, 0, belt, L - 1, 1, stripe); rect(x, 0, Hh - 4, L - 1, 1, stripe);
  rect(x, 2, wy, L - 10, wb, '#ffe2a0');                                              // lit windows, passengers
  for (let i = 2; i < L - 9; i += 5) rect(x, i, wy, 1, wb, tint(up, '#000000', 0.4));
  for (let i = 4; i < L - 10; i += 5) if (hash(i, L, 97) < 0.55) { rect(x, i, wy + wb - 3, 2, 3, '#3a2a30'); px(x, i, wy + wb - 4, '#2a1e24'); }
  rect(x, L - 7, wy - 1, 6, wb + 2, '#1c2a44'); px(x, L - 3, wy, '#6a8ac0');           // the windscreen
  rect(x, L - 22, 1, 13, 7, '#141018'); text(x, num, L - 21, 2, '#ffb030');   // the line's number over the door
  rect(x, L - 15, belt + 1, 4, Hh - belt - 4, '#2a2a38');                             // the front door
  for (let i = 4; i < L - 18; i += 6) px(x, i, belt + 2, stripe);                     // a line of filete flourishes
  for (let i = 7; i < L - 18; i += 12) { px(x, i, belt + 3, stripe); px(x, i + 1, belt + 2, '#f4e6b0'); }
  const ww = Math.max(4, Math.round(L * 0.1));
  for (const f of [0.14, 0.72]) { rect(x, Math.round(L * f), Hh - 3, ww, 3, '#050408'); px(x, Math.round(L * f) + 1, Hh - 2, '#6a6a74'); }
  px(x, L - 1, Hh - 5, '#fff6d8'); px(x, L - 1, Hh - 6, '#fff6d8'); px(x, 0, Hh - 5, '#ff3a3a');
  return { c, w: L, h: Hh, front: [L - 1, Hh - 5], back: [0, Hh - 5], bus: true };
}
function mirror(v) {                                  // the same vehicle facing left (numbers re-drawn the right way round)
  const [c, x] = makeCanvas(v.w, v.h);
  x.scale(-1, 1); x.drawImage(v.c, -v.w, 0); x.setTransform(1, 0, 0, 1, 0, 0);
  const fl = p => p && [v.w - 1 - p[0], p[1]];
  return { ...v, c, front: fl(v.front), back: fl(v.back), flipped: true };
}
function relabel(v, num) {                            // after a mirror: write the number again so it reads
  const x = v.c.getContext('2d');
  rect(x, 9, 1, 13, 7, '#141018'); text(x, num, 10, 2, '#ffb030');
  return v;
}
const BUS_RUNS = [                                    // the scheduled colectivos: lane, livery, window
  { lane: 2, liv: 0, a: 14, b: 26 }, { lane: 3, liv: 1, a: 94, b: 106 }, { lane: 2, liv: 2, a: 174, b: 186 },
  { lane: 1, liv: 3, a: 54, b: 70 }, { lane: 0, liv: 2, a: 134, b: 150 }, { lane: 1, liv: 1, a: 214, b: 230 },
];
let VEH;
function buildVehicles() {
  VEH = LANES.map(L => {
    const face = v => (L.dir > 0 ? v : mirror(v));
    return {
      cars: CAR_COLS.map(([b, d]) => face(makeCar(L.s, b, d, false))), taxi: face(makeCar(L.s, '#14141c', '#0a0a10', true)),
      buses: BUS_LIVERY.map(lv => { const v = face(makeColectivo(L.s, lv)); return L.dir > 0 ? v : relabel(v, lv[3]); }),
    };
  });
}
const laneX = (L, u) => (L.dir > 0 ? lerp(-90, W + 90, u) : lerp(W + 90, -90, u));
function drawVehicle(v, L, x, t) {
  const x0 = Math.round(x - v.w / 2), y0 = L.y - v.h + 1;
  // its mirror image on the wet asphalt
  const n = Math.min(v.h, (L.dir < 0 ? MED0 : ROAD1) - L.y - 2);
  if (n > 0) {
    ctx.globalAlpha = 0.22; ctx.setTransform(1, 0, 0, -1, 0, 2 * L.y + 2);
    ctx.drawImage(v.c, 0, v.h - n, v.w, n, x0, L.y - n + 1, v.w, n); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1;
  }
  ctx.drawImage(v.c, x0, y0);
  const fx = x0 + v.front[0], fy = y0 + v.front[1], bx = x0 + v.back[0];
  halo(HL.head, fx + L.dir * 3, fy, 0.75); halo(HL.tail, bx, fy, 0.5);
  if (v.bus) halo(HL.busWin, x0 + (v.w >> 1), y0 + Math.round(v.h * 0.35), 0.6);
  ctx.globalAlpha = 0.35; ctx.fillStyle = '#fff0c8';                                       // the headlights' glare on the wet road
  ctx.fillRect(Math.min(fx, fx + L.dir * 14), L.y + 1, 14, 1); ctx.globalAlpha = 0.18; ctx.fillRect(Math.min(fx, fx + L.dir * 22), L.y + 2, 22, 1);
  ctx.fillStyle = '#ff5a4a'; ctx.globalAlpha = 0.25; ctx.fillRect(Math.min(bx, bx - L.dir * 8), L.y + 1, 8, 1);
  ctx.globalAlpha = 1;
}
function drawLane(t, li) {
  const L = LANES[li], V = VEH[li];
  for (let k = 0; k < L.slots; k++) {
    const ph = k / L.slots + hash(li, k, 3) * 0.12;
    const q = t * L.n / LOOP + ph, f = Math.floor(q), cyc = ((f % L.n) + L.n) % L.n, u = q - f;
    const t0 = (f - ph) * LOOP / L.n, t1 = t0 + LOOP / L.n;
    if (BUS_RUNS.some(s => s.lane === li && t0 < s.b + 6 && t1 > s.a - 6)) continue;
    const hv = hash(li * 7 + k, cyc, L.seed + 20);
    if (hv < 0.22) continue;
    const v = hv < 0.6 ? V.taxi : V.cars[Math.floor(hash(li, cyc, k + 40) * V.cars.length)];
    drawVehicle(v, L, laneX(L, u), t);
  }
  for (const s of BUS_RUNS) {
    if (s.lane !== li || t < s.a || t >= s.b) continue;
    drawVehicle(V.buses[s.liv], L, laneX(L, (t - s.a) / (s.b - s.a)), t);
  }
}

/* ================= People ================= */
// A figure is built from a few measurements that follow from its height, so the same person can stand at any depth.
// put(i, j, col): i counted from the centre column, j from the feet row (0), negative upwards.
// S: { h, coat, legs, skin, hair, hat: 'cap' | 'fedora', hatCol, long, longHair, bag, pack, scarf, beard, apron, vest,
//      hold: 'mate' | 'cup' | 'thermos' | 'phone' }
// pose: view 0 side (facing right) / 1 back / 2 front; stride -1 | 0 | 1; bob; arm 0 swing / 1 held out / 2 one up /
// 3 both up / 4 out to the sides / 5 hands together (clapping); sit
const dims = h => { const hh = h >= 22 ? 5 : h >= 15 ? 4 : 3, th = Math.round(h * 0.36); return { hh, hw: h >= 20 ? 4 : 3, tw: h >= 22 ? 6 : h >= 15 ? 5 : 4, th, lh: h - hh - th }; };
const SHOE = '#0e0c12';
function drawFigure(put, S, { view = 0, stride = 0, bob = 0, arm = 0, sit = false } = {}) {
  const { hh, hw, tw, th, lh } = dims(S.h), big = S.h >= 20, lw = tw >= 5 ? 2 : 1, x0 = -((tw - 1) >> 1);
  const coatD = darker(S.coat), legsD = darker(S.legs, 0.3), sh = Math.max(3, Math.round(lh * 0.55));
  const R = (i, j, w, h, col) => { for (let y = j; y < j + h; y++) for (let x = i; x < i + w; x++) put(x, y, col); };
  const tb = sit ? -(sh + 1) : -lh - bob, tt = tb - th + 1;
  if (sit) {
    R(x0 + tw - 2, -sh - 1, 4, 2, S.legs); R(x0 + tw + 1, -sh + 1, lw, sh - 1, S.legs); R(x0 + tw + 1, 0, lw + 1, 1, SHOE);
  } else if (view === 0) {
    const half = lh >> 1, ext = big && stride === 1 ? 1 : 0;
    for (const [bx, s, col] of [[lw === 2 ? -1 : 0, -stride, legsD], [1, stride, S.legs]]) {
      R(bx + s, -lh + 1 - bob, lw, lh - half + bob, col);
      R(bx + s + Math.sign(s) * ext, -half + 1, lw, half - 1, col);
      R(bx + s + Math.sign(s) * ext, 0, lw + 1, 1, SHOE);
    }
  } else {
    const xl = x0 + (tw >= 5 ? 0 : 1), xr = x0 + tw - lw - (tw >= 5 ? 0 : 1);
    for (const [bx, col] of [[xl, view === 1 ? S.legs : legsD], [xr, S.legs]]) { R(bx, -lh + 1 - bob, lw, lh - 1 + bob, col); R(bx, 0, lw, 1, SHOE); }
  }
  R(x0, tt, tw, th, S.coat);
  if (view === 0) R(x0, tt, 1, th, coatD); else { R(x0, tt + 1, 1, th - 1, coatD); R(x0 + tw - 1, tt + 1, 1, th - 1, coatD); }
  if (S.long && !sit) { const sk = S.long === true ? S.coat : S.long, n = Math.max(2, Math.round(lh * 0.45)); R(x0, tb + 1, tw, n, sk); R(x0, tb + 1, 1, n, darker(sk)); R(x0, tb + n, tw, 1, darker(sk)); }
  else R(x0, tb, tw, 1, coatD);
  if (S.apron && !sit) { const n = Math.round(lh * 0.75); R(x0 + (view === 0 ? 1 : 0), tb - 2, tw - (view === 0 ? 0 : 0), n + 2, '#eeeae2'); R(x0, tb - 2, 1, n + 2, '#c8c4bc'); }
  if (S.vest) { R(x0 + (view === 0 ? 0 : 0), tt + 1, view === 0 ? 2 : 1, th - 1, S.vest); if (view !== 0) R(x0 + tw - 1, tt + 1, 1, th - 1, S.vest); }
  if (S.scarf) R(x0 + 1, tt, tw - 2, 1, S.scarf);
  if (S.bow) put(x0 + (view === 0 ? tw - 1 : tw >> 1), tt, '#14101a');
  if (S.pack) { if (view === 0) R(x0 - 1 - (big ? 1 : 0), tt + 1, 1 + (big ? 1 : 0), th - 2, S.pack); else if (view === 1) R(x0 + 1, tt + 1, tw - 2, th - 2, S.pack); }
  if (S.bag) { if (view === 0) R(x0 - 1, tb - 2, 2 + (big ? 1 : 0), 3, S.bag); else R(x0 + tw - 1, tb - 2, 2, 3, S.bag); }
  if (S.thermos && view !== 1) R(x0 - (view === 0 ? 0 : 1), tt + 1, 2, Math.max(3, th - 2), '#d8d4cc');   // under the arm
  // arms
  const fx = x0 + tw;
  if (arm === 0 && view === 0) { const ax = x0 + 1 + (stride === 1 ? 0 : stride === -1 ? tw - 3 : (tw >> 1) - 1); R(ax, tt + 1, 1, th - 2, coatD); put(ax, tb - (sit ? 1 : 0), S.skin); }
  else if (arm === 1) {
    R(fx - 1, tt + 2, 2 + (big ? 1 : 0), 1, coatD); const hx = fx + 1 + (big ? 1 : 0); put(hx, tt + 2, S.skin);
    if (S.hold === 'mate') { put(hx, tt + 1, '#8a5a2a'); put(hx + 1, tt + 1, '#6a4020'); put(hx, tt, '#3a5a2a'); put(hx + 1, tt - 1, '#c8c8d0'); }
    else if (S.hold === 'cup') { put(hx, tt + 1, '#f4f0ea'); put(hx + 1, tt + 1, '#d8d2ca'); }
    else if (S.hold === 'phone') { put(hx, tt + 1, '#cfe0ff'); }
  } else if (arm === 2) {
    if (view === 0) { put(fx - 1, tt, coatD); put(fx, tt - 1, coatD); put(fx, tt - 2, coatD); put(fx + 1, tt - 3, S.skin); }
    else { R(x0 - 1, tt - 2, 1, 4, coatD); R(fx, tt - 2, 1, 4, coatD); put(x0 - 1, tt - 3, S.skin); put(fx, tt - 3, S.skin); }
  } else if (arm === 3) { R(x0 - 1, tt - 3, 1, 4, coatD); R(fx, tt - 3, 1, 4, coatD); put(x0 - 1, tt - 4, S.skin); put(fx, tt - 4, S.skin); }
  else if (arm === 4) { R(x0 - 2, tt + 1, 2, 1, S.coat); R(fx, tt + 1, 2, 1, S.coat); }
  else if (arm === 5) { put(fx, tt + 2, coatD); put(fx + 1, tt + 1, S.skin); if (view !== 0) { put(x0 - 1, tt + 2, coatD); put(x0, tt + 1, S.skin); } }
  // head
  const hb = tt - 1, ht = hb - hh + 1, hx0 = view === 0 ? x0 + tw - hw - (tw - hw >= 2 ? 1 : 0) : x0 + ((tw - hw) >> 1), fh = hh >= 4 ? hh - 2 : 2;
  R(hx0, ht, hw, hh, S.hair);
  if (view === 0) { R(hx0 + hw - 2, hb - fh + 1, 2, fh, S.skin); if (S.beard) R(hx0 + hw - 2, hb - 1, 2, 2, S.beard); }
  else if (view === 2) { R(hx0 + (hw === 4 ? 1 : 0), hb - fh + 1, hw === 4 ? 2 : 3, fh, S.skin); if (S.beard) R(hx0 + (hw === 4 ? 1 : 0), hb, hw === 4 ? 2 : 3, 1, S.beard); }
  if (S.longHair) { if (view === 0) R(hx0, hb + 1, 1 + (big ? 1 : 0), 3, S.hair); else if (view === 1) R(hx0, hb + 1, hw, 3, S.hair); }
  if (S.hat === 'cap') { R(hx0, ht, hw, hh >= 5 ? 2 : 1, S.hatCol); if (view === 0) put(hx0 + hw, ht + (hh >= 5 ? 1 : 0), S.hatCol); }
  else if (S.hat === 'fedora') { R(hx0 - 1, ht, hw + 2, 1, S.hatCol); R(hx0, ht - 1, hw, 1, S.hatCol); put(hx0, ht - 1, darker(S.hatCol)); R(hx0, ht, hw, 1, S.band || '#3a2a2a'); }
  // the street's warm light along the shoulders and the hair's crown
  put(x0 + (view === 0 ? tw - 1 : 1), tt, tint(S.coat, '#ffd8a0', 0.35));
  if (!S.hat) put(hx0 + (view === 0 ? hw - 1 : 1), ht, tint(S.hair, '#ffd8a0', 0.3));
}
const SPR_W = 22, SPR_AX = 10;                        // every figure sprite: 22 wide, the centre column at 10
function figure(S, pose, flip = false) {
  const hgt = S.h + 9, p = painter(SPR_W, hgt);
  drawFigure((i, j, col) => p.put(flip ? SPR_W - 1 - SPR_AX - i : SPR_AX + i, hgt - 1 + j, col), S, pose);
  const c = p.done(); c.flip = flip;
  return c;
}
const blit = (spr, x, fy) => ctx.drawImage(spr, Math.round(x) - (spr.flip ? SPR_W - 1 - SPR_AX : SPR_AX), fy - spr.height + 1);
const WALK_POSES = [{ bob: 1 }, { stride: 1 }, { bob: 1 }, { stride: -1 }];
function walkSet(S, flip, arm = 0) { const a = figure(S, { ...WALK_POSES[0], arm }, flip); return [a, figure(S, { ...WALK_POSES[1], arm }, flip), a, figure(S, { ...WALK_POSES[3], arm }, flip)]; }
function handAt(S) { const { tw, th, lh } = dims(S.h); return { x: -((tw - 1) >> 1) + tw + 1 + (S.h >= 20 ? 1 : 0), y: -lh - th + 3 }; }
function shadow(x, y, w) { ctx.globalAlpha = 0.32; ctx.fillStyle = '#06050c'; ctx.fillRect(Math.round(x) - (w >> 1), y + 1, w, 1); ctx.globalAlpha = 1; }

const COATS = ['#2a3456', '#6a2a34', '#3a5a3a', '#d8c8a8', '#3a3a44', '#b84a3a', '#2e6a8a', '#8a6a4a', '#e6e2da', '#5a3a6a', '#c8a03a',
  '#1e2230', '#a85a7a', '#4a7a8a', '#7a8a4a', '#d87a4a', '#74acdf'];
const HAIRS = ['#141014', '#2a1a14', '#4a2e1a', '#8a5a2a', '#c8a060', '#9a9aa2', '#5a1a14', '#141014'];
const SKINS = ['#e8b890', '#d8a07a', '#c88a64', '#f0c8a0', '#a8704a'];
const LEGC = ['#16121c', '#20243a', '#2a2e3e', '#3a3a4a', '#4a4038', '#c8c0b0', '#1e2a2a', '#2a3a5a'];
const of = (r, list) => list[Math.floor(r() * list.length)];
function randomStyle(r, h) {
  const S = { h, coat: of(r, COATS), legs: of(r, LEGC), skin: of(r, SKINS), hair: of(r, HAIRS) };
  const v = r(), b = r();
  if (v < 0.12) { S.hat = 'cap'; S.hatCol = of(r, COATS); }
  if (r() < 0.25) S.long = r() < 0.5 ? true : of(r, COATS);
  if (r() < 0.36) S.longHair = true;
  if (b < 0.26) S.bag = of(r, ['#e8d8b0', '#d0343e', '#1e2230', '#f0c040', '#8a5a2a']); else if (b < 0.38) S.pack = of(r, ['#c8483a', '#2e4a7a', '#3a3a44', '#4a7a4a']);
  if (r() < 0.14) S.scarf = of(r, ['#74acdf', '#f0c040', '#e8e0d0', '#c84a6a']);
  return S;
}
const mk = (h, coat, legs, hair, extra = {}) => ({ h, coat, legs, hair, skin: '#e8b890', ...extra });
const beat = (t, ph = 0) => Math.floor(t * 4 / 3 + ph) & 1;          // 80 beats a minute, like the music

/* ================= Walkers on our sidewalk: each crosses n times a loop on his own row ================= */
const WALKERS = [];
{
  const r = rng(1906);
  const rows = [222, 225, 228, 259, 263, 267];
  let unit = -1;
  const add = (y, n, ph, dir, S, extra = {}, same = false) => { if (!same) unit++; WALKERS.push({ y, n, ph, dir, S, rank: spread(unit), ...extra }); };
  rows.forEach((y, i) => {
    const h = personH(y), dir = i & 1 ? 1 : -1;
    for (let k = 0; k < 2; k++) {
      const n = 2 + Math.floor(r() * 3), ph = r(), S = randomStyle(r, h + (r() < 0.3 ? -1 : 0)), d = k ? -dir : dir;
      const v = r();
      if (v < 0.22) { S.hold = 'mate'; S.thermos = true; add(y, n, ph, d, S, { arm: 1 }); }   // mate in hand, the thermos under the arm
      else if (v < 0.36) { S.hold = 'cup'; add(y, n, ph, d, S, { arm: 1 }); }
      else if (v < 0.46) { S.hold = 'phone'; add(y, n, ph, d, S, { arm: 1, phone: true }); }
      else add(y, n, ph, d, S);
      if (r() < 0.3) add(y, n, ph + (5 + h * 0.3) / (W + 40), d, randomStyle(r, h - 1), {}, true);       // a couple
    }
  });
}
function buildWalkers() {
  for (const w of WALKERS) w.spr = walkSet(w.S, w.dir < 0, w.arm || 0);
}
function drawWalker(w, i, t, fr) {
  const span = W + 40, u = frac(t * w.n / LOOP + w.ph), x = Math.round(w.dir > 0 ? -20 + u * span : W + 20 - u * span);
  if (x < -14 || x > W + 14) return;
  const f = (fr + i * 3) & 3;
  shadow(x, w.y, 5);
  blit(w.spr[f], x, w.y);
  if (w.phone) { const hd = handAt(w.S); halo(HL.phone, x + w.dir * hd.x, w.y + hd.y + 1, 0.5); }
}

/* ================= Small figures further off: the plaza, the far sidewalk, the theatre letting out ================= */
// 2 to 3 px wide, 8 to 11 px tall, two-frame stride
function drawSmall(x, y, h, coat, legs, fr, still = false) {
  x = Math.round(x);
  const lh = Math.round(h * 0.42), th = h - lh - 2;
  ctx.fillStyle = legs;
  if (still || (fr & 1)) { ctx.fillRect(x, y - lh + 1, 1, lh); ctx.fillRect(x + 1, y - lh + 1, 1, lh); }
  else { ctx.fillRect(x - 1, y - lh + 2, 1, lh - 1); ctx.fillRect(x + 1, y - lh + 1, 1, lh); ctx.fillRect(x, y - lh + 1, 1, 1); }
  ctx.fillStyle = coat; ctx.fillRect(x, y - lh - th + 1, 2, th);
  ctx.fillStyle = '#d8a882'; ctx.fillRect(x, y - lh - th - 1, 2, 2);
}
const PLAZA_FOLK = [0, 1, 2, 3, 4, 5, 6, 7].map(k => ({ y: [167, 169, 172, 174, 166, 171, 173, 168][k], n: 2 + (k % 3), ph: k * 0.137 + 0.05, dir: k & 1 ? 1 : -1,
  coat: COATS[(k * 5 + 2) % COATS.length], legs: LEGC[k % LEGC.length], rank: spread(k) }));
// tourists on the lawn photographing the Obelisco: [x, y, flash times]
const SNAPPERS = [{ x: 206, y: 173, f: [33.4, 128.6, 188.2], rank: 0.15 }, { x: 278, y: 172, f: [71.2, 156.8], rank: 0.45 }, { x: 290, y: 174, f: [12.6, 116.3, 210.4], rank: 0.8 }];
function drawPlazaFolk(t, fr, crowd) {
  for (const P of PLAZA_FOLK) {
    if (!seen(P.rank, crowd)) continue;
    const u = frac(t * P.n / LOOP + P.ph), x = P.dir > 0 ? lerp(140, 340, u) : lerp(340, 140, u);
    const d = Math.hypot((x - OB.x) / 92, (P.y - 170) / 7.5);
    if (d > 1.05 || Math.abs(x - OB.x) < 12) continue;                  // on the lawn only, round the Obelisco
    drawSmall(x, P.y, 9 + ((P.y - 166) >> 2), P.coat, P.legs, fr + (P.y & 1));
  }
  for (const S of SNAPPERS) {
    if (!seen(S.rank, crowd)) continue;
    drawSmall(S.x, S.y, 11, '#d87a4a', '#20243a', 0, true);
    px(ctx, S.x + (S.x < OB.x ? 2 : -1), S.y - 8, '#cfe0ff');            // the phone held up
    for (const ft of S.f) if (t >= ft && t < ft + 0.25) { px(ctx, S.x + (S.x < OB.x ? 2 : -1), S.y - 8, '#ffffff'); halo(HL.flash, S.x + 1, S.y - 8, 0.8 * (1 - (t - ft) / 0.25)); }
  }
}
const FAR_FOLK = [0, 1, 2, 3, 4, 5, 6, 7, 8].map(k => ({ n: 3 + (k % 4), ph: k * 0.111 + 0.03, dir: k % 2 ? 1 : -1, y: 162 + (k % 3),
  coat: COATS[(k * 7 + 1) % COATS.length], rank: spread(k + 3) }));
const THEATRE_OUT = { a: 160, b: 206, doors: [MARQ.x + 10, MARQ.x + 29, MARQ.x + 48] };
const AUDIENCE = [];
{ const r = rng(161); for (let k = 0; k < 18; k++) AUDIENCE.push({ t0: THEATRE_OUT.a + 2 + k * 1.6 + r() * 1.2, door: k % 3, dir: r() < 0.5 ? -1 : 1, v: 9 + r() * 7, y: 161 + Math.floor(r() * 3), coat: of(r, COATS), rank: k < 6 ? CAST : spread(k) }); }
function drawFarFolk(t, fr, crowd) {
  for (const F of FAR_FOLK) {
    if (!seen(F.rank, crowd)) continue;
    const u = frac(t * F.n / LOOP + F.ph), x = F.dir > 0 ? lerp(-8, W + 8, u) : lerp(W + 8, -8, u);
    if (x > CORR.x0 - 2 && x < CORR.x1 + 2 && F.y < 162) continue;
    drawSmall(x, F.y, 9, F.coat, '#1e1c28', fr + F.n);
  }
  // the show lets out: people step out of the doors under the marquee and drift off along the sidewalk
  if (t >= THEATRE_OUT.a && t < THEATRE_OUT.b + 30) for (const A of AUDIENCE) {
    if (!seen(A.rank, crowd) || t < A.t0 || t > A.t0 + 34) continue;
    const dt = t - A.t0, x = THEATRE_OUT.doors[A.door] + A.dir * dt * A.v * 0.6;
    if (x < -8 || x > W + 8) continue;
    ctx.globalAlpha = Math.min(1, dt * 1.5, (A.t0 + 34 - t) / 3); drawSmall(x, A.y, 9, A.coat, '#1a1820', fr + A.door); ctx.globalAlpha = 1;
  }
}

/* ================= The tango on the corner: a couple, a bandoneon, a few people watching ================= */
// The couple walk the tango slowly back and forth over a few paces of sidewalk, he walking forward, she back; they
// turn (he passes round her) at each end of their walk, every 20 s. On every eighth beat she flicks a heel back (a
// boleo); four times a loop they stop, he lunges and she leans back into a long dip, and the people watching clap.
const DIPS = [36, 108, 180, 228];
const dipAt = t => { for (const d of DIPS) if (t >= d - 2 && t < d + 5) return d; return -1; };
const SUIT = '#181824', SUIT_L = '#2a2a3a', DRESS = '#c4243a', DRESS_D = '#86182a', STOCK = '#2a1a22', HEEL = '#e0303e';
function drawCouple(t) {
  const { x: cx, y } = TANGO, walk = Math.sin(TAU * 6 * t / LOOP), dirSign = Math.cos(TAU * 6 * t / LOOP) >= 0 ? 1 : -1;
  const d = dipAt(t), dip = d < 0 ? 0 : smooth(d - 0.3, d + 1, t) * (1 - smooth(d + 3.2, d + 4.6, t));
  const still = d >= 0;                                 // they stop walking for the dip
  const b = Math.floor(t * 4 / 3), stride = still ? 0 : (b & 1 ? 1 : -1), kick = !still && (b & 7) === 6;
  const mx = Math.round(cx + walk * 9 - dirSign * 2), dir = dirSign, wx = mx + dir * 4;
  const R = (ox, fdir, i, j, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(fdir > 0 ? ox + i : ox - i - w + 1, y + j, w, h); };
  shadow(mx + dir * 2, y, 11);
  // him: dark suit, white shirt, a fedora; he lunges for the dip
  const M = (i, j, w, h, col) => R(mx, dir, i, j, w, h, col), lo = Math.round(dip * 2), lean = Math.round(dip * 2);
  if (dip > 0.3) {
    M(-2, -9 + lo, 2, 9 - lo, '#101018'); M(-3, -1, 2, 1, SHOE);                                   // back leg, straight
    M(1, -9 + lo, 2, 4, SUIT); M(3, -5 + lo, 2, 5 - lo, SUIT); M(3, 0, 3, 1, SHOE);                 // front leg, bent
  } else {
    M(-1 - stride, -9, 2, 5, '#101018'); M(-1 - stride, -4, 2, 4, '#101018'); M(-1 - stride, 0, 3, 1, SHOE);
    M(1, -9, 2, 5, SUIT); M(1 + stride, -4, 2, 4, SUIT); M(1 + stride, 0, 3, 1, SHOE);
  }
  for (let j = -17; j <= -10; j++) { const o = Math.round(lean * (-10 - j) / 7); M(-2 + o, j + lo, 5, 1, j === -17 ? SUIT_L : SUIT); }
  M(2 + lean, -17 + lo, 1, 3, '#e8e4dc'); M(2 + lean, -16 + lo, 1, 1, '#8a1a2a');                   // shirt and tie
  M(-1 + lean, -21 + lo, 2, 4, '#141014'); M(1 + lean, -21 + lo, 2, 4, '#e0ae86'); M(1 + lean, -18 + lo, 1, 1, '#c8946e');
  M(-2 + lean, -22 + lo, 6, 1, '#0e0e14'); M(-1 + lean, -24 + lo, 4, 2, '#16161e'); M(-1 + lean, -23 + lo, 4, 1, '#4a3a34');
  M(3 + lean, -14 + lo, 3, 1, SUIT); M(6 + lean, -14 + lo, 1, 1, '#e0ae86');                        // his arm round her back
  // her: a red dress with a slit, dark stockings, red heels, her hair up; she leans back for the dip
  const F = (i, j, w, h, col) => R(wx, -dir, i, j, w, h, col), wl = -Math.round(dip * 4);
  if (dip > 0.3) {
    F(-1, -8, 1, 8, STOCK); F(-2, 0, 2, 1, HEEL);                                                     // standing leg
    for (let k = 0; k < 5; k++) F(1 + k, -7 + Math.round(k * 1.5), 1, 1, STOCK);                    // the other slides out to him
    F(6, -1, 1, 1, HEEL);
  } else if (kick) {
    F(1, -8, 1, 8, STOCK); F(1, 0, 2, 1, HEEL);
    F(-1, -8, 1, 3, STOCK); F(-2, -6, 1, 1, STOCK); F(-3, -7, 1, 1, STOCK); F(-4, -8, 1, 1, STOCK); F(-5, -9, 1, 1, HEEL);
  } else {
    F(-1 + stride, -8, 1, 4, STOCK); F(-1 + stride, -4, 1, 4, STOCK); F(-1 + stride, 0, 2, 1, HEEL);
    F(1 - stride, -8, 1, 8, STOCK); F(1 - stride, 0, 2, 1, HEEL);
  }
  for (let j = -12; j <= -7; j++) { const o = Math.round(wl * (-7 - j) / 12), fl = j === -7 ? 1 : 0; F(-2 - fl + o, j, 5 + fl, 1, j === -7 ? DRESS_D : DRESS); }
  F(1, -8, 1, 1, STOCK);                                                                              // the slit
  for (let j = -17; j <= -13; j++) { const o = Math.round(wl * (-7 - j) / 12); F(-1 + o, j, 3, 1, DRESS); }
  const ho = Math.round(wl * 1.15);
  F(0 + ho, -17, 2, 1, '#e8b890');                                                                     // bare shoulders
  F(-1 + ho, -21, 2, 4, '#141014'); F(1 + ho, -21, 1, 4, '#e8b890'); F(-2 + ho, -21, 1, 2, '#141014'); F(-1 + ho, -22, 1, 1, '#e8303e');   // the bun, a flower
  F(2 + ho, -17, 1, 1, '#e8b890'); F(3 + Math.round(ho / 2), -18, 1, 1, '#e8b890');                  // her hand on his shoulder
  M(3 + lean, -19 + lo, 1, 2, SUIT); M(3 + lean, -20 + lo, 1, 1, '#e0ae86');                        // their joined hands
}
// the bandoneon player on a kitchen chair, his hat upturned on the sidewalk
let BANDO_SPR, NOTE_SPR;
function buildBando() {
  const S = mk(personH(BANDO.y), '#e8e4dc', '#1c1c26', '#9a9aa2', { vest: '#2a2a34', hat: 'fedora', hatCol: '#2a2a34', beard: '#b8b8c0', skin: '#d8a07a' });
  BANDO_SPR = figure(S, { sit: true, arm: 1 });
  NOTE_SPR = [sprite(['.#', '.#', '##', '##'], { '#': '#ffe9a0' }), sprite(['.##', '.#.', '##.', '##.'], { '#': '#ffd070' })];
}
function drawChair(x, y) {
  rect(ctx, x - 4, y - 7, 7, 1, '#6a4a2a'); rect(ctx, x - 4, y - 6, 1, 6, '#4a3020'); rect(ctx, x + 2, y - 6, 1, 6, '#3a2418');
  rect(ctx, x - 5, y - 15, 1, 9, '#4a3020'); rect(ctx, x - 5, y - 15, 1, 1, '#6a4a2a');
}
function drawBando(t, crowd) {
  const { x, y } = BANDO;
  drawChair(x - 1, y);
  rect(ctx, x - 14, y - 1, 6, 2, '#1c1a22'); rect(ctx, x - 13, y - 2, 4, 1, '#2a2832');              // the hat on the ground
  if (crowd <= 0) return;
  if (hash(5, step(t, 480), 12) < 0.4) px(ctx, x - 12 + (step(t, 240) % 3), y - 1, '#ffe070');
  shadow(x, y, 9);
  blit(BANDO_SPR, x, y);
  // the bandoneon on his knees: two wooden ends with pearl buttons, the bellows breathing in and out
  const bw = 1 + Math.round(1.5 + 1.5 * Math.sin(TAU * 60 * t / LOOP)), bx = x - 1, by = y - 11;
  rect(ctx, bx, by, 3, 5, '#4a2c1a'); rect(ctx, bx, by, 3, 1, '#7a4c2c'); px(ctx, bx + 1, by + 2, '#e8e0d0');
  for (let i = 0; i < bw; i++) rect(ctx, bx + 3 + i, by + 1, 1, 4, i & 1 ? '#d8ccb8' : '#2a1a1e');   // the bellows
  rect(ctx, bx + 3 + bw, by, 3, 5, '#4a2c1a'); rect(ctx, bx + 3 + bw, by, 3, 1, '#7a4c2c'); px(ctx, bx + 4 + bw, by + 2, '#e8e0d0');
  px(ctx, bx - 1, by + 2, '#d8a07a'); px(ctx, bx + 6 + bw, by + 2, '#d8a07a');                      // his hands on the ends
  const fast = dipAt(t) >= 0;
  for (let k = 0; k < 3; k++) {                         // notes drifting up
    const { cyc, u } = cycle(t, fast ? 96 : 60, k / 3 + 0.07 * k);
    ctx.globalAlpha = Math.sin(Math.PI * u) * 0.85;
    ctx.drawImage(NOTE_SPR[(cyc + k) & 1], Math.round(x + 4 + hash(k, cyc, 13) * 10 - 5 + Math.sin(u * 6 + k) * 2), Math.round(y - 26 - u * 16));
  }
  ctx.globalAlpha = 1;
}
// people watching: [x, feet row, faces right?, holds], the clap after each dip
const WATCHERS = [[140, 232, 1, 'mate'], [190, 234, -1, 'cup'], [205, 240, -1, null], [128, 252, 1, null], [214, 250, -1, 'phone']];
[2, 0, 3, 1, 4].forEach((place, i) => { WATCHERS[i].rank = (place + 0.5) / 5; });
function buildWatchers() {
  const r = rng(808);
  WATCHERS.forEach((Wt, i) => {
    const S = randomStyle(r, personH(Wt[1]) - (i === 2 ? 1 : 0));
    S.hold = Wt[3]; if (Wt[3] === 'mate') S.thermos = true;
    const flip = Wt[2] < 0;
    Wt.S = S; Wt.spr = [figure(S, { arm: Wt[3] ? 1 : 0 }, flip), figure(S, { arm: 5 }, flip), figure(S, { arm: Wt[3] ? 1 : 0, bob: 1 }, flip)];
  });
}
function drawWatcher(Wt, i, t) {
  const d = dipAt(t), clap = d >= 0 && t > d + 3.4 && t < d + 7 && beat(t * 2, i * 0.5);
  const sway = Math.round(wave(t, 20 + i * 4, i * 0.37) * 0.7), sip = Wt[3] && Wt[3] !== 'phone' && frac(t / 16 + i * 0.3) < 0.12;
  shadow(Wt[0] + sway, Wt[1], 5);
  blit(clap ? Wt.spr[1] : sip ? Wt.spr[2] : Wt.spr[0], Wt[0] + sway, Wt[1]);
  if (Wt[3] === 'phone') { const hd = handAt(Wt.S); halo(HL.phone, Wt[0] + sway + Wt[2] * hd.x, Wt[1] + hd.y + 1, 0.55); }
}
// two friends on a bench sharing a mate: it passes from one to the other every 12 s, the thermos tips to refill it
const BENCH = { x: 262, y: 254 };
let MATE_SPR;
function buildMate() {
  const r = rng(515), h = personH(BENCH.y);
  const A = { ...randomStyle(r, h), bag: null, pack: null, hat: null }, B = { ...randomStyle(r, h - 1), bag: null, pack: null, longHair: true };
  MATE_SPR = { a: [figure(A, { sit: true }), figure(A, { sit: true, arm: 1 })], b: [figure(B, { sit: true }, true), figure(B, { sit: true, arm: 1 }, true)] };
}
function drawBench(t, crowd) {
  const { x, y } = BENCH;
  rect(ctx, x - 12, y - 8, 26, 1, '#5a3a22'); rect(ctx, x - 12, y - 7, 26, 1, '#3a2416');           // the bench
  rect(ctx, x - 12, y - 13, 26, 1, '#5a3a22'); rect(ctx, x - 12, y - 11, 26, 1, '#4a2e1a');
  for (const lx of [x - 11, x + 12]) rect(ctx, lx, y - 13, 1, 13, '#1a1a22');
  if (crowd <= 0 || !seen(0.3, crowd)) return;
  // in every 12 s one of them holds it (A in the odd ones), drinks, and passes it over at the end
  const q = frac(t / 12), toB = Math.floor(t / 12) & 1, pass = q > 0.8, from = toB ? x - 1 : x + 2, to = toB ? x + 2 : x - 1;
  blit(MATE_SPR.a[pass || toB ? 1 : 0], x - 6, y);
  blit(MATE_SPR.b[pass || !toB ? 1 : 0], x + 7, y);
  const hy = y - 13, mx = Math.round(lerp(from, to, smooth(0.82, 0.98, q)));
  px(ctx, mx, hy, '#8a5a2a'); px(ctx, mx, hy - 1, '#3a5a2a'); px(ctx, mx + 1, hy - 2, '#c8c8d0');   // the gourd and its bombilla
  rect(ctx, x - 2, y - 11, 2, 4, '#d8d4cc'); px(ctx, x - 2, y - 12, '#8a8a94');                       // the thermos between them
  if (q > 0.6 && q < 0.75) { ctx.globalAlpha = 0.5; px(ctx, mx, hy - 3 - Math.floor((q - 0.6) * 40) % 3, '#e8e8f0'); ctx.globalAlpha = 1; }   // steam
}

/* ================= The parrilla's tables on the sidewalk and its waiter ================= */
const TABLES = [{ x: 330, y: 236, rank: CAST }, { x: 374, y: 232, rank: 0.35 }, { x: 352, y: 256, rank: CAST }, { x: 404, y: 252, rank: 0.7 }];
const NEAR_X = 452;                                   // the near corner building's left edge (the pizzeria)
let DINERS, TABLE_C, CHAIR_C, WAITER_SPR;
function buildTables() {
  const r = rng(2468);
  DINERS = TABLES.map(T => {
    const h = personH(T.y) - 1, A = { ...randomStyle(r, h), bag: null, pack: null }, B = { ...randomStyle(r, h), bag: null, pack: null };
    return [[figure(A, { sit: true, arm: 1 }), figure(A, { sit: true, arm: 2 })], [figure(B, { sit: true, arm: 1 }, true), figure(B, { sit: true, arm: 2 }, true)]];
  });
  TABLE_C = sprite(['.wwwwwww.', 'cccccccc.', '...kk....', '...kk....', '...kk....', '...kk....', '...kk....', '..kkkk...'], { w: '#f4f0e8', c: '#b8b4ac', k: '#1a1820' });
  CHAIR_C = [sprite(['b...', 'b...', 'b...', 'b...', 'bbbb', 'b..b', 'b..b', 'b..b'], { b: '#3a2416' }), sprite(['...b', '...b', '...b', '...b', 'bbbb', 'b..b', 'b..b', 'b..b'], { b: '#3a2416' })];
  const S = mk(personH(244), '#f0ece4', '#16161e', '#2a1a14', { apron: true, vest: '#16161e', bow: true, skin: '#d8a07a' });
  WAITER_SPR = { in: walkSet(S, true, 2), out: walkSet(S, false), serve: figure(S, { arm: 1 }, true), S };
}
function drawTable(T, k, t, crowd) {
  const x = T.x, y = T.y, here = seen(T.rank, crowd);
  ctx.drawImage(CHAIR_C[0], x - 10, y - 7); ctx.drawImage(CHAIR_C[1], x + 7, y - 7);
  if (here) {
    const [A, B] = DINERS[k], sa = hash(k, step(t, 80), 31) < 0.3, sb = hash(k + 9, step(t + 1.3, 80), 31) < 0.3;
    blit(A[sa ? 1 : 0], x - 6, y); blit(B[sb ? 1 : 0], x + 7, y);
  }
  ctx.drawImage(TABLE_C, x - 4, y - 7);
  // on the table: a candle in a glass, and whatever was served (cups, a glass of wine, a plate of meat)
  px(ctx, x, y - 8, '#c8b8a0'); const fl = hash(k, step(t, 1440), 33);
  px(ctx, x, y - 9, fl < 0.25 ? '#fff4c0' : '#ffc050'); halo(HL.candle, x, y - 9, 0.7 + 0.3 * fl);
  if (here) {
    if (k & 1) { px(ctx, x - 3, y - 8, '#f4f0ea'); px(ctx, x + 3, y - 8, '#f4f0ea'); }
    else { px(ctx, x - 3, y - 9, '#6a1424'); px(ctx, x - 3, y - 8, '#c8c8d4'); rect(ctx, x + 2, y - 8, 3, 1, '#8a4a2a'); }
  }
}
const WAITER = { y: 244, every: 40, t0: 2, speed: 15 };
function drawWaiter(t, fr) {
  const k = Math.floor((t - WAITER.t0 + LOOP) % LOOP / WAITER.every), q = (t - WAITER.t0 + LOOP) % LOOP - k * WAITER.every;
  const T = TABLES[k % TABLES.length], tx = T.x + 9, x0 = W + 14, dIn = (x0 - tx) / WAITER.speed, stay = 4.5;
  const S = WAITER_SPR.S, hd = handAt(S);
  let x;
  if (q < dIn) {                                        // out with the tray held high
    x = lerp(x0, tx, q / dIn); shadow(x, WAITER.y, 5); blit(WAITER_SPR.in[(fr + 1) & 3], x, WAITER.y);
    const ty = WAITER.y - S.h - 3, txx = Math.round(x) - 2;
    rect(ctx, txx - 2, ty, 6, 1, '#b8bcc8'); px(ctx, txx - 1, ty - 1, '#f4f0ea'); px(ctx, txx + 2, ty - 1, '#6a1424'); px(ctx, txx + 2, ty - 2, '#c8c8d4');
  } else if (q < dIn + stay) {                          // sets it down
    x = tx; shadow(x, WAITER.y, 5); blit(WAITER_SPR.serve, x, WAITER.y);
    rect(ctx, x - hd.x - 2, WAITER.y + hd.y, 5, 1, '#b8bcc8');
  } else if (q < dIn * 2 + stay) {                      // and back, the empty tray under his arm
    x = lerp(tx, x0, (q - dIn - stay) / dIn); shadow(x, WAITER.y, 5); blit(WAITER_SPR.out[(fr + 3) & 3], x, WAITER.y);
    rect(ctx, Math.round(x) - 3, WAITER.y - Math.round(S.h * 0.55), 1, 4, '#b8bcc8');
  }
}

/* ================= The pizzeria on the near corner: slices by the counter, a man eating standing up ================= */
const PZ_SERVE = 24, PZ_T0 = 6;
let PIZZERO_SPR, EATER_SPR, NEAR_C;
function renderNear() {
  const p = painter(W - NEAR_X, H), put = (i, y, col) => p.put(i - NEAR_X, y, col), r = rng(3141);
  for (let y = 0; y < H; y++) for (let i = NEAR_X; i < W; i++) {
    let v = 0.18 + 0.3 * Math.pow(y / H, 1.6) + (hash(i, y, 41) - 0.5) * 0.06;
    if (i === NEAR_X) v += 0.25; else if (i === NEAR_X + 1) v += 0.1;
    put(i, y, pick(STONE, v, i, y));
  }
  for (let fy = 6; fy < 190; fy += 32) {               // tall windows with iron balconies, a few lit, plants on the rails
    for (const wx of [NEAR_X + 6, NEAR_X + 18]) {
      const lit = r() < 0.4;
      for (let y = fy; y < fy + 20; y++) for (let i = 0; i < 7; i++) put(wx + i, y, lit ? (y < fy + 4 ? '#ffe6ac' : i === 0 || i === 6 ? '#c8803a' : '#f4b866') : (i === 0 ? '#22264a' : '#0e1024'));
      for (let i = -2; i < 9; i++) { put(wx + i, fy + 20, '#0c0a12'); put(wx + i, fy + 24, '#0c0a12'); if ((i & 1) === 0) for (let y = fy + 21; y < fy + 24; y++) put(wx + i, y, '#0c0a12'); }
      for (let i = -1; i < 8; i += 2) if (r() < 0.6) { put(wx + i, fy + 19, of(r, ['#2a5a2a', '#3a6a2a', '#c83a4a', '#e8a0c0'])); put(wx + i, fy + 18, '#2a4a2a'); }
      for (let i = -1; i < 8; i++) put(wx + i, fy - 1, STONE[6]);
    }
  }
  for (let y = 198; y < 212; y++) for (let i = NEAR_X; i < W; i++) put(i, y, y === 198 || y === 211 ? '#3a1a1a' : '#f4ead0');   // the sign band
  for (let y = 214; y < 246; y++) for (let i = NEAR_X + 2; i < W; i++) {                              // the counter window
    let col = y < 236 ? (y < 216 ? '#fff4d8' : '#ffd890') : '#e8e2d8';
    if (y >= 236 && y < 238) col = y === 236 ? '#fffaf0' : '#c8c0b0';                                  // the marble counter
    if (y >= 238) col = pick(['#4a1a1a', '#6a2424', '#8a3030'], 0.5 + (hash(i >> 1, y, 43) - 0.5) * 0.4, i, y);
    put(i, y, col);
  }
  for (let y = 216; y < 236; y++) put(NEAR_X + 2, y, '#5a3a2a');
  for (let i = NEAR_X + 4; i < W - 2; i += 9) {         // trays of pizza on the shelf behind the glass
    for (let j = 0; j < 7; j++) { put(i + j, 233, '#8a8a94'); put(i + j, 232, j % 3 === 1 ? '#c8302a' : '#f0c040'); put(i + j, 231, j === 0 || j === 6 ? '#d89a48' : j % 2 ? '#f6d860' : '#e8b030'); }
  }
  return p.done();
}
// upstairs over the pizzeria: a lamp-lit room someone crosses now and then, a television's flicker, a curtain stirring
function drawNearFx(t, crowd) {
  const rx = NEAR_X + 18, ry = 70;
  rect(ctx, rx + 1, ry, 5, 20, '#f4b866'); rect(ctx, rx + 1, ry, 5, 4, '#ffe6ac');
  const cur = Math.round(1 + wave(t, 16, 0.2) + 0.6 * wave(t, 43, 0.7));                    // the curtain in the open window
  rect(ctx, rx + 1, ry, cur, 20, '#e8d0b0'); px(ctx, rx + cur, ry + 19, '#c8a888');
  const u = frac(t / 30 + 0.15);
  if (crowd > 0 && u < 0.4) { const fx = rx + 1 + Math.round(u / 0.4 * 6) - 1; if (fx >= rx + 1 && fx <= rx + 5) { rect(ctx, fx, ry + 7, 2, 13, '#3a2a2a'); rect(ctx, fx, ry + 4, 2, 3, '#2a1e1e'); } }
  const tx = NEAR_X + 6, ty = 134, tv = step(t, 1440);
  rect(ctx, tx + 1, ty + 4, 5, 16, ['#5a78b8', '#8aa4dc', '#3e4e8c', '#b4c4ec', '#6a5a9c'][Math.floor(hash(3, tv >> 1, 8) * 5)]);
  rect(ctx, tx + 1, ty, 5, 4, '#24284a');
}
function buildPizza() {
  const S = mk(personH(240), '#f4f0ea', '#1c1c26', '#2a1a14', { hat: 'cap', hatCol: '#f4f0ea', skin: '#d8a07a' });
  PIZZERO_SPR = [figure(S, { view: 2 }), figure(S, { view: 2, arm: 4 })];
  const E = mk(personH(PIZZA.y), '#3a4a6a', '#2a2a34', '#4a2e1a', { skin: '#e8b890' });
  EATER_SPR = [figure(E, {}), figure(E, { arm: 2 }), figure(E, { arm: 1 })];
}
function drawPizza(t, fr, crowd) {
  const q = ((t - PZ_T0) % PZ_SERVE + PZ_SERVE) % PZ_SERVE;
  halo(HL.oven, W - 6, 222, 0.75 + 0.25 * wave(t, 300, 0.2) * wave(t, 77, 0.4));
  if (crowd > 0) {
    // the pizzero behind the glass, the counter cutting him at the waist; he brings a slice on the peel at q 2..4
    ctx.save(); ctx.beginPath(); ctx.rect(NEAR_X + 3, 214, W - NEAR_X - 3, 22); ctx.clip();
    blit(PIZZERO_SPR[q > 2 && q < 4.5 ? 1 : 0], W - 12, 250);
    ctx.restore();
  }
  if (q > 3 && q < 6.5 && crowd > 0) {                   // the slice on the counter, steaming
    const sx = Math.round(lerp(W - 14, NEAR_X + 6, smooth(3, 4.2, q)));
    rect(ctx, sx, 235, 4, 1, '#f0c040'); px(ctx, sx + 1, 234, '#c8302a'); px(ctx, sx + 3, 235, '#d89a48');
    ctx.globalAlpha = 0.4; px(ctx, sx + 1 + (step(t, 960) & 1), 232 - Math.floor(frac(t * 2) * 3), '#f0f0f8'); ctx.globalAlpha = 1;
  }
  if (!seen(0.2, crowd)) return;
  // the man eating at the counter: takes the new slice, then a bite every couple of seconds
  const ex = PIZZA.x + 8, bite = q > 6.5 && Math.floor(q / 1.6) % 2 === 0;
  shadow(ex, PIZZA.y, 5);
  blit(EATER_SPR[q > 5 && q < 6.5 ? 2 : bite ? 1 : 0], ex, PIZZA.y);
  if (q > 6.5 && q < 20) { const hy = PIZZA.y - EATER_SPR[0].height + 9 + (bite ? -1 : 9); px(ctx, ex + (bite ? 3 : 4), hy, '#f0c040'); }
}

/* ================= The kiosco: a booth crammed with sweets and magazines ================= */
const KIOSK_VISITS = [20, 80, 140, 200];
let KIOSK_C, KIOSQUERO_SPR, KCUST;
function renderKiosco() {
  const { x, y, w } = KIOSCO, top = y - 40, p = painter(W, H), put = p.put, r = rng(2020);
  for (let j = top; j < top + 8; j++) for (let i = x - 1; i < x + w + 1; i++) put(i, j, j === top || j === top + 7 ? '#8a1a22' : '#f8f2e4');   // the lit sign
  for (let j = top + 8; j <= y; j++) for (let i = x; i < x + w; i++) {
    let col = i === x || i === x + w - 1 ? '#1a3a2a' : '#245238';
    if (j > top + 9 && j < y - 10 && i > x + 1 && i < x + w - 2) col = j < top + 12 ? '#fff4d8' : '#ffd890';   // the lit window
    if (j >= y - 10 && j < y - 8) col = '#e8e0cc';                                                             // the ledge
    put(i, j, col);
  }
  for (let j = top + 13; j < y - 11; j += 4) for (let i = x + 3; i < x + w - 3; i++) {                         // shelves of sweets
    put(i, j + 2, '#8a5a3a');
    if (hash(i, j, 21) < 0.75) put(i, j + 1, of(r, ['#e83a4a', '#f0c040', '#4ab0e8', '#f4f0ea', '#8a4a2a', '#e86ab0', '#5ac85a', '#3a2a24']));
  }
  for (let i = x + 2; i < x + w - 2; i += 3) { put(i, y - 11, of(r, ['#f0c040', '#e83a4a', '#4ab0e8'])); put(i + 1, y - 11, '#c8c0b0'); }   // jars on the ledge
  for (let j = y - 7; j < y - 1; j++) { put(x + 4, j, '#e8303e'); put(x + w - 6, j, '#f0c040'); }              // stickers
  for (const [mx, col] of [[x - 3, '#e84a6a'], [x + w + 1, '#4ab0e8']]) for (let j = top + 11; j < top + 24; j++) { put(mx, j, col); put(mx + 1, j, j % 5 === 0 ? '#f4f0ea' : col); }   // magazines on the sides
  const c = p.done();
  text(p.x, 'KIOSCO', x + ((w - textWidth('KIOSCO')) >> 1), top + 2, '#c8202e');
  return c;
}
function buildKiosco() {
  const r = rng(4242);
  KIOSQUERO_SPR = [figure(mk(personH(KIOSCO.y) - 2, '#5a6a8a', '#2a2a34', '#9a9aa2', { skin: '#e0b088' }), { view: 2 }), figure(mk(personH(KIOSCO.y) - 2, '#5a6a8a', '#2a2a34', '#9a9aa2', { skin: '#e0b088' }), { view: 2, arm: 1 })];
  KCUST = KIOSK_VISITS.map(() => { const S = randomStyle(r, personH(240)); return { walk: [walkSet(S, false), walkSet(S, true)], back: figure(S, { view: 1 }), pay: figure(S, { view: 1, arm: 2 }) }; });
}
function drawKiosco(t, fr, crowd) {
  const { x, y, w } = KIOSCO;
  put1(KIOSK_C);
  halo(HL.kiosk, x + (w >> 1), y - 20, 0.9);
  if (crowd > 0) {                                      // the kiosquero behind the ledge, looking up when someone comes
    ctx.save(); ctx.beginPath(); ctx.rect(x + 2, y - 30, w - 4, 20); ctx.clip();
    const v = KIOSK_VISITS.findIndex(a => t >= a && t < a + 11), serve = v >= 0 && t - KIOSK_VISITS[v] > 3.5 && t - KIOSK_VISITS[v] < 6.5;
    blit(KIOSQUERO_SPR[serve ? 1 : 0], x + 20 + Math.round(wave(t, 12, 0.3) * 3), y - 6);
    ctx.restore();
  }
}
function drawKioskCustomer(t, fr) {
  const v = KIOSK_VISITS.findIndex(a => t >= a && t < a + 11);
  if (v < 0) return;
  const q = t - KIOSK_VISITS[v], C = KCUST[v], X = KIOSCO.x + 24, Y = 240;
  if (q < 3) { const x = lerp(-12, X, q / 3); shadow(x, Y, 5); blit(C.walk[0][(fr + v) & 3], x, Y); }
  else if (q < 8) { shadow(X, Y, 5); blit(q > 4 && q < 6.5 ? C.pay : C.back, X, Y); }
  else { const x = lerp(X, -12, (q - 8) / 3); shadow(x, Y, 5); blit(C.walk[1][(fr + v) & 3], x, Y); }
}

/* ================= The dog walker and his pack ================= */
// a paseador with five dogs on leads fanned out ahead of him, twice a loop along the front of the sidewalk
const DOGWALK = { y: 257, runs: [[40, 82, 1], [150, 192, -1]] };
const DOG_DEFS = [   // rows (two trot frames), colours, place ahead of him [dx, dy]
  { rows: [['.......ee', '......ddd', 'tddddddd.', '.dddddd..', '.d....d..'], ['.......ee', '......ddd', 'tddddddd.', '.dddddd..', '..d..d...']], d: '#c8963e', e: '#8a5a20', at: [13, -3] },
  { rows: [['.....e.', '....ddd', 'tdddd..', '.d..d..'], ['.....e.', '....ddd', 'tdddd..', '..dd...']], d: '#1c1a20', e: '#0c0a10', at: [10, 2] },
  { rows: [['......e.', '.....ddd', 'tddddd..', '.dddd...', '.d...d..'], ['......e.', '.....ddd', 'tddddd..', '.dddd...', '..d.d...']], d: '#e8e4dc', e: '#5a4a40', at: [19, 0] },
  { rows: [['....e', '...dd', 'dddd.', '.d.d.'], ['....e', '...dd', 'dddd.', '..d..']], d: '#8a5a3a', e: '#4a2a1a', at: [7, 4] },
  { rows: [['........ee', '.......ddd', 'tdddddddd.', '.ddddddd..', '.d.....d..', '.d.....d..'], ['........ee', '.......ddd', 'tdddddddd.', '.ddddddd..', '..d...d...', '..d...d...']], d: '#6a6a72', e: '#3a3a42', at: [24, 5] },
];
let DOG_SPR, WALKER_DOGS;
function buildDogs() {
  DOG_SPR = DOG_DEFS.map(D => D.rows.map(rows => [sprite(rows, { d: D.d, e: D.e, t: D.d }), sprite(rows, { d: D.d, e: D.e, t: D.d }, true)]));
  const S = mk(personH(DOGWALK.y), '#3a5a8a', '#2a2a34', '#2a1a14', { hat: 'cap', hatCol: '#c8303a', pack: '#2a2a34', skin: '#d8a07a' });
  WALKER_DOGS = { walk: [walkSet(S, false, 1), walkSet(S, true, 1)], S };
}
function drawDogWalk(t, fr) {
  const run = DOGWALK.runs.find(([a, b]) => t >= a && t < b);
  if (!run) return;
  const [a, b, dir] = run, u = (t - a) / (b - a), x = dir > 0 ? lerp(-34, W + 10, u) : lerp(W + 34, -10, u), y = DOGWALK.y;
  const hd = handAt(WALKER_DOGS.S), hx = Math.round(x) + dir * hd.x, hy = y + hd.y;
  DOG_DEFS.forEach((D, k) => {                          // the dogs first: the ones behind him in the row order
    const sniff = hash(k, step(t, 60), 71) < 0.25 ? -3 : 0, f = (fr + k) & 1;
    const dx = Math.round(x + dir * (D.at[0] + sniff + Math.round(wave(t, 30 + k * 7, k * 0.2) * 1.5))), dy = y + D.at[1];
    const spr = DOG_SPR[k][f][dir > 0 ? 0 : 1], sx = dx - (spr.width >> 1), sy = dy - spr.height + 1;
    if (D.at[1] <= 0) { ctx.drawImage(spr, sx, sy); ctx.globalAlpha = 0.7; line(ctx, hx, hy, dir > 0 ? sx + spr.width - 2 : sx + 1, sy + 1, '#6a2a34'); ctx.globalAlpha = 1; }
    D.spr = spr; D.sx = sx; D.sy = sy;
  });
  shadow(x, y, 6);
  blit(WALKER_DOGS.walk[dir > 0 ? 0 : 1][fr & 3], x, y);
  DOG_DEFS.forEach(D => { if (D.at[1] > 0) { ctx.drawImage(D.spr, D.sx, D.sy); ctx.globalAlpha = 0.7; line(ctx, hx, hy, dir > 0 ? D.sx + D.spr.width - 2 : D.sx + 1, D.sy + 1, '#2a3a5a'); ctx.globalAlpha = 1; } });
}

/* ================= A neighbour shakes out a tablecloth on a balcony ================= */
const CLOTH = { a: 70, b: 82 };
function drawNeighbour(t, crowd) {
  const lit = env(t, CLOTH.a - 4, CLOTH.b + 4, 1.5);
  if (lit <= 0) return;
  const x = BALC.x, y = BALC.y;
  ctx.globalAlpha = lit; rect(ctx, x, y, 3, 5, '#f4b866'); rect(ctx, x, y, 3, 1, '#ffe6ac'); ctx.globalAlpha = 1;
  if (crowd <= 0 || t < CLOTH.a || t >= CLOTH.b) return;
  rect(ctx, x + 1, y + 1, 2, 4, '#2a1a2a'); px(ctx, x + 1, y, '#3a2a24');
  const flap = step(t, 1440) & 1;
  rect(ctx, x + 3, y + 1 + flap, 3, 2 - flap, '#f4f0ea'); px(ctx, x + 6, y + 2 - flap, '#e8e0d8');
}

/* ================= Street lamps: tall ones on the median, a cast-iron farola on our corner ================= */
const FAROLA = { x: 300, base: 252 };
function renderFarola() {                             // 11 x 66: a fluted post, a four-paned lantern on a crook
  const p = painter(11, 66), put = p.put;
  for (let y = 12; y < 66; y++) { put(5, y, '#1a1a24'); put(4, y, y > 56 ? '#2a2a36' : '#24242e'); if (y > 58) { put(3, y, '#1a1a24'); put(6, y, '#14141c'); } }
  for (let i = 2; i <= 8; i++) put(i, 65, '#1a1a24');
  for (let i = 1; i <= 9; i++) put(i, 1, '#1a1a24');
  for (let y = 2; y < 10; y++) for (let i = 2; i <= 8; i++) put(i, y, i === 2 || i === 8 ? '#1a1a24' : i === 5 ? '#e8c890' : y < 4 ? '#fffbe8' : '#ffe6a8');
  put(5, 0, '#1a1a24'); for (let i = 3; i <= 7; i++) put(i, 10, '#1a1a24'); put(5, 11, '#1a1a24');
  return p.done();
}
function renderAveLamps() {                           // the avenue's tall lamps, on the median: a mast and a double head
  const p = painter(W, H), put = p.put;
  for (const L of AVE_LAMPS) {
    for (let y = L.y; y < MED0 + 1; y++) { put(L.x, y, '#3a3a4a'); put(L.x + 1, y, '#1e1e2a'); }
    for (const s of [-1, 1]) for (let k = 1; k <= 7; k++) put(L.x + (s > 0 ? 1 : 0) + s * k, L.y - (k < 3 ? k : 2), '#3a3a4a');
    for (const s of [-1, 1]) { const hx = L.x + (s > 0 ? 1 : 0) + s * 8; put(hx - 1, L.y - 2, '#4a4a5a'); put(hx, L.y - 2, '#4a4a5a'); put(hx + 1, L.y - 2, '#4a4a5a'); put(hx, L.y - 1, '#fff4d0'); put(hx - 1, L.y - 1, '#ffe0a0'); put(hx + 1, L.y - 1, '#ffe0a0'); }
  }
  return p.done();
}

/* ================= The jacaranda over the corner: its trunk, and the canopy filling the top left ================= */
function renderTrunk() {
  const p = painter(W, H), put = p.put, { x, base } = NEAR_TREE;
  for (let y = 60; y <= base - 2; y++) {
    const w = y > 200 ? 6 : y > 140 ? 5 : 4, xo = Math.round(Math.sin(y * 0.045) * 2 - (base - y) * 0.03);
    for (let i = 0; i < w; i++) put(x + xo + i - (w >> 1), y, i === 0 ? '#4a3a40' : i === 1 ? '#34282e' : i === w - 1 ? '#0e0a10' : '#1e161c');
  }
  // branches up into the canopy
  for (const [bx, by, ex, ey] of [[88, 96, 40, 52], [92, 84, 150, 46], [90, 120, 132, 82], [88, 110, 52, 76], [96, 72, 110, 30]]) {
    for (let k = 0; k <= 60; k++) { const f = k / 60, xx = lerp(bx, ex, f), yy = lerp(by, ey, f) + Math.sin(f * 3.1) * 3; put(xx, yy, '#241a20'); put(xx, yy + 1, '#120c12'); if (f < 0.5) put(xx + 1, yy, '#1a1218'); }
  }
  return p.done();
}
function renderCanopy() {                             // drawn in three bands so it can sway; lit from below by the street
  const p = painter(210, 96);
  const blobs = [[20, 8, 34, 20], [62, 14, 34, 18], [104, 10, 32, 16], [146, 16, 30, 14], [36, 40, 30, 16], [80, 44, 30, 16],
    [122, 40, 26, 14], [160, 34, 20, 12], [10, 52, 18, 14], [58, 66, 20, 12], [104, 64, 18, 12], [140, 56, 16, 10], [178, 20, 18, 10]];
  for (const [cx, cy, rx, ry] of blobs) for (let y = -ry; y <= ry; y++) for (let i = -rx; i <= rx; i++) {
    const X = cx + i, Y = cy + y;
    if (Math.hypot(i / rx, y / ry) > 1 - hash(X, Y, 401) * 0.35) continue;
    let v = 0.42 + (y / ry) * 0.22 + (vnoise(X / 3, Y / 3, 402) - 0.5) * 0.7 - (Y < 20 ? 0.15 : 0);
    if (hash(X, Y, 403) < 0.06) { p.put(X, Y, hash(X, Y, 404) < 0.5 ? '#1e2e22' : '#2a3e2a'); continue; }   // the fern-like leaves
    p.put(X, Y, pick(JAC, v, X, Y));
    if (v > 0.68 && hash(X, Y, 405) < 0.1) JAC_SPARK.push({ x: X - 10, y: Y - 8, k: 20 + Math.floor(hash(X, Y, 5) * 50), ph: hash(X, Y, 6), canopy: true });
  }
  // a few clusters hanging under the edge
  for (let k = 0; k < 18; k++) { const X = 6 + Math.floor(hash(k, 1, 406) * 180), Y = 60 + Math.floor(hash(k, 2, 406) * 22); for (let j = 0; j < 4; j++) for (let i = -1; i <= 1; i++) if (Math.abs(i) + j / 2 < 2) p.put(X + i, Y + j, pick(JAC, 0.55 + j * 0.08, X + i, Y + j)); }
  return p.done();
}
function drawCanopy(t) {
  const sw = wave(t, 12, 0.1) * 1.2 + wave(t, 37, 0.6) * 0.6;
  for (let k = 0; k < 4; k++) ctx.drawImage(CANOPY_C, 0, k * 24, 210, 24, -10 + Math.round(sw * (4 - k) / 3), -8 + k * 24, 210, 24);
  for (const s of JAC_SPARK) if (wave(t, s.k, s.ph) > 0.6) px(ctx, s.canopy ? s.x + Math.round(sw * (4 - Math.floor((s.y + 8) / 24)) / 3) : s.x, s.y, '#e8d4ff');
}

/* ================= Jacaranda blossoms at three depths ('blossoms') ================= */
const BLOSSOMS = [
  { count: 150, nMin: 26, nMax: 40, a: 0.7, amp: 3, drift: 14, y1: [150, 178] },
  { count: 110, nMin: 14, nMax: 22, a: 0.95, amp: 6, drift: 26, y1: [220, 262] },
  { count: 34, nMin: 16, nMax: 22, a: 1, amp: 10, drift: 44, y1: [H + 6, H + 6] },
];
const BL_COLS = ['#9a7ad8', '#b896e8', '#7a5ac0', '#c8a8f0', '#a884e0'];
BLOSSOMS.forEach((L, li) => {
  const r = rng(900 + li);
  L.list = [];
  for (let i = 0; i < L.count; i++) L.list.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), k: 20 + Math.floor(r() * 50), p: r(), kf: 90 + Math.floor(r() * 90), pf: r() });
});
function drawBlossoms(li, t) {
  const L = BLOSSOMS[li];
  for (const f of L.list) {
    const { cyc, u } = cycle(t, f.n, f.ph), sway = Math.sin(TAU * (f.k * t / LOOP + f.p));
    const yEnd = lerp(L.y1[0], L.y1[1], hash(f.id, cyc, 2));
    let x0, y0;
    if (li === 0) { x0 = -20 + hash(f.id, cyc, 1) * (W + 20); y0 = -4 + hash(f.id, cyc, 3) * 40; }    // off the far trees, through the city's glow
    else { x0 = -20 + hash(f.id, cyc, 1) * (li === 2 ? 230 : W); y0 = li === 2 ? 20 + hash(f.id, cyc, 3) * 50 : -4 + hash(f.id, cyc, 3) * 70 * (x0 < 190 ? 1 : 0.2); }
    const flutter = Math.sin(TAU * (f.kf * t / LOOP + f.pf));      // a falling flower spins and flutters as it goes
    const x = x0 + u * L.drift + (L.amp * sway + (li + 1) * 1.6 * flutter) * Math.min(1, u * 3), y = lerp(y0, yEnd, u) - Math.abs(flutter) * (li + 1) * 0.8;
    ctx.globalAlpha = L.a * Math.min(1, u * 8) * (li < 2 && u > 0.88 ? (1 - u) / 0.12 : 1);
    ctx.fillStyle = BL_COLS[(f.id + cyc) % BL_COLS.length];
    if (li === 2) {                                     // a whole trumpet flower, tumbling
      const flat = Math.cos(TAU * (f.k * 2 * t / LOOP + f.p)) > 0;
      ctx.fillRect(Math.round(x), Math.round(y), flat ? 3 : 2, flat ? 2 : 3); ctx.fillStyle = '#e4d0ff'; ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
    } else if (li === 1 && (f.id & 1)) ctx.fillRect(Math.round(x), Math.round(y), 2, 1);
    else ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= The rooftop sign: an alfajor under the moon ================= */
// A steel frame on the low block right of the Obelisco, over the corner of Corrientes: two legs, a catwalk, two lamps
// on goose necks. The city's own ad sells ALFAJOR LUNITA, a chocolate alfajor that loses a bite every 8 s under a
// crescent moon. render(t, { pitch }) paints the site's face over it; billboard: false leaves the whole thing out.
const BB_LAMPS = [BB.x + 12, BB.x + 38];
const BB_FLICK = [[47, 48.2], [171.5, 172.6]];
const BBC = { x: BB.x - 3, y: BB.y - 7, w: BB.w + 6, h: 124 - BB.y + 8 };
function renderBillboard() {
  const p = painter(BBC.w, BBC.h), put = (i, y, col) => p.put(i - BBC.x, y - BBC.y, col);
  const X0 = BB.x - 2, X1 = BB.x + BB.w + 1, Y0 = BB.y - 2, Y1 = BB.y + BB.h + 1;
  for (const lx of [BB.x + 5, BB.x + 32]) for (let y = Y1 + 1; y <= 124; y++) { put(lx, y, '#4a4856'); put(lx + 1, y, '#22202c'); }
  for (let i = BB.x + 5; i <= BB.x + 33; i++) { const f = (i - BB.x - 5) / 28; put(i, Math.round(lerp(Y1 + 1, 123, f)), '#2e2c38'); put(i, Math.round(lerp(123, Y1 + 1, f)), '#2e2c38'); }
  for (let i = X0; i <= X1; i++) { put(i, Y1 + 1, '#3a3846'); put(i, Y1 + 2, '#1a1822'); if ((i & 3) === 0) put(i, Y1, '#2a2834'); }   // catwalk
  for (let i = X0; i <= X1; i++) { put(i, Y0, '#2a2834'); put(i, Y0 + 1, '#5a5866'); put(i, Y1, '#1a1822'); put(i, Y1 - 1, '#3a3846'); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, '#2a2834'); put(X0 + 1, y, '#5a5866'); put(X1, y, '#1a1822'); put(X1 - 1, y, '#3a3846'); }
  for (const lx of BB_LAMPS) { for (let y = Y0 - 4; y < Y0; y++) put(lx + 2, y, '#3a3846'); put(lx + 1, Y0 - 5, '#3a3846'); put(lx, Y0 - 5, '#3a3846'); put(lx - 1, Y0 - 4, '#5a5866'); put(lx, Y0 - 4, '#5a5866'); }
  return p.done();
}
const AD_STARS = [[44, 2], [47, 9], [20, 2], [33, 17], [46, 16], [2, 17]];
function renderAd() {
  const { w, h } = BB, p = painter(w, h);
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) p.put(i, y, pick(['#1c2a5a', '#14204a', '#0e163a'], y / h * 0.8 + i / w * 0.4, i, y));
  const c = p.done();
  text(p.x, 'ALFAJOR', 19, 4, '#f6ead0'); text(p.x, 'LUNITA', 21, 11, '#74acdf');
  return c;
}
// the alfajor seen a little from above: a glazed top, the side with its band of dulce de leche; bites out of its right
function drawAlfajor(x, y, bites) {
  for (let j = 0; j < 11; j++) for (let i = 0; i < 14; i++) {
    const dx = i - 6.5, top = j < 5, ry = top ? 2.4 : 2.4, cy = top ? 2.5 : 7.5;
    const inTop = (dx * dx) / 44 + ((j - 2.5) * (j - 2.5)) / (ry * ry) <= 1, inSide = j >= 2.5 && j <= 7.5 && Math.abs(dx) <= 6.6, inBot = (dx * dx) / 44 + ((j - cy) * (j - cy)) / (ry * ry) <= 1;
    if (!(inTop || inSide || inBot)) continue;
    let bitten = false;
    for (let b = 0; b < bites; b++) { const bx = 13 - b * 3, by = 3 + b * 2; if (Math.hypot(i - bx, j - by) < 2.6) bitten = true; }
    if (bitten) continue;
    let col = inTop && j <= 3 ? (dx < -2 && j < 2 ? '#8a5034' : '#6a3a24') : j === 5 || j === 6 ? '#ecbc74' : '#3a1e14';
    if (inTop && j === 4) col = '#4a2818';
    px(ctx, x + i, y + j, col);
  }
  if (bites) px(ctx, x + 12 - (bites - 1) * 3, y + 5, '#f8d898');                                       // the filling where it was bitten
}
function drawAd(t) {
  ctx.drawImage(AD_C, BB.x, BB.y);
  drawAlfajor(BB.x + 3, BB.y + 6, step(t, 30) % 3);
  // a crescent moon over it, breathing
  const ma = 0.75 + 0.25 * wave(t, 24, 0.2), mx = BB.x + 4, my = BB.y + 2;
  ctx.globalAlpha = ma; ctx.fillStyle = '#ffe9a0';
  ctx.fillRect(mx + 1, my - 1, 3, 1); ctx.fillRect(mx, my, 2, 1); ctx.fillRect(mx, my + 1, 1, 1); ctx.fillRect(mx, my + 2, 2, 1); ctx.fillRect(mx + 1, my + 3, 3, 1);
  ctx.globalAlpha = 1;
  AD_STARS.forEach(([sx, sy], k) => { if (wave(t, 40 + k * 9, k * 0.23) > 0.2) px(ctx, BB.x + sx, BB.y + sy, k % 2 ? '#fff6d8' : '#a8c0ff'); });
}
function bbLampI(t, i) {
  let v = 0.95 + 0.05 * wave(t, 1800, 0.25 + i * 0.33);
  if (i === 1 && inEvent(t, BB_FLICK)) { const hv = hash(17, Math.floor(t * 24)); v *= hv > 0.5 ? 1 : hv > 0.22 ? 0.4 : 0.1; }
  return v;
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 4, 0.8);
  ctx.drawImage(BB_C, BBC.x, BBC.y);
  if (!PITCH.covers(t, pitch)) drawAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => {
    const li = bbLampI(t, i);
    if (li < 0.9) { ctx.globalAlpha = (0.9 - li) * 0.5; rect(ctx, lx - 10, BB.y, 21, 9, '#06060e'); ctx.globalAlpha = 1; }
    ctx.fillStyle = li > 0.6 ? '#fff6d8' : li > 0.2 ? '#c8a070' : '#4a3a30';
    ctx.fillRect(lx - 1, BB.y - 5, 2, 1);
    halo(HL.bbLamp, lx, BB.y - 4, li);
  });
}

/* ================= The picked weather (site/js/engine/weather.js) ================= */
// Buenos Aires draws only its own jacaranda blossoms, so every pick is the engine's: far drops fall behind the
// Obelisco onto the far side of the avenue, the middle ones onto our sidewalk, the nearest past the bottom.
const WEATHER = weatherPlans(['blossoms']);
const WX_LAND = [[150, 178], [220, 262], [H + 4, H + 4]];
const WX_LIGHTS = [
  ...AVE_LAMPS.flatMap(L => [{ x: L.x - 8, y: L.y + 12, rx: 12, ry: 22, col: '#ffe6b0' }, { x: L.x + 9, y: L.y + 12, rx: 12, ry: 22, col: '#ffe6b0' }]),
  { x: FAROLA.x, y: FAROLA.base - 50, rx: 18, ry: 22, col: '#ffdca0' }, { x: KIOSCO.x + 20, y: KIOSCO.y - 22, rx: 26, ry: 16, col: '#ffe6b8' },
  { x: 466, y: 226, rx: 20, ry: 16, col: '#ffd890' }, { x: MARQ.x + 32, y: MARQ.y + 4, rx: 38, ry: 12, col: '#ffe08a' },
  { x: OB.x, y: 120, rx: 14, ry: 50, col: '#eef0ff' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 4, rx: 30, ry: 10, col: '#fff0c8' }] };

/* ================= Vignette ================= */
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.8, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 4; d[o + 1] = 4; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.4 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Per-frame light: windows, marquees, signs, the Obelisco ================= */
function drawCityFx(t, crowd) {
  for (const w of WIN_TOG) if (hash(w.id + 50, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < 0.45) {
    ctx.fillStyle = '#ffe6ac'; ctx.fillRect(w.x, w.y, 3, 1);
    ctx.fillStyle = '#f4b866'; ctx.fillRect(w.x, w.y + 1, 3, w.h - 1);
  }
  for (const f of FIGWIN) {                             // someone crossing a lit room
    const u = frac(t * f.k / LOOP + f.ph);
    if (u < 0.3 && crowd > 0) { ctx.fillStyle = '#3a2a2a'; ctx.fillRect(f.x + Math.floor(u / 0.3 * 3), f.y + 1, 1, f.h - 1); }
  }
  const tv = step(t, 1440);
  TV_WIN.forEach((w, k) => { ctx.fillStyle = ['#5a78b8', '#8aa4dc', '#3e4e8c', '#b4c4ec', '#6a5a9c'][Math.floor(hash(k, tv >> (k & 1), 8) * 5)]; ctx.fillRect(w.x, w.y, 3, w.h); });
  // the theatre's marquee: bulbs chasing round it; the blade sign's neon and its border
  const ch = step(t, 960);
  for (const b of MARQ_BULBS) { const on = ((b.k + ch) & 3) !== 0; px(ctx, b.x, b.y, on ? '#fff2b8' : '#8a6a3a'); }
  halo(HL.marq, MARQ.x + 32, MARQ.y + 7, 0.9);
  const bl = step(t, 480);
  for (let j = 0; j < BLADE.h; j += 3) px(ctx, BLADE.x, BLADE.y + j, ((j / 3 + bl) & 1) ? '#ffd890' : '#6a3a2a'), px(ctx, BLADE.x + BLADE.w - 1, BLADE.y + j, ((j / 3 + bl + 1) & 1) ? '#ffd890' : '#6a3a2a');
  halo(HL.blade, BLADE.x + 4, BLADE.y + 18, 0.8);
  for (const b of CORR_MARQ) if (wave(t, 90 + Math.floor(b.ph * 60), b.ph) > -0.2) px(ctx, b.x, b.y, '#fff4c8');
  halo(HL.corr, CORR_VP.x, CORR_VP.y, 0.9);
  for (const s of SHOPS) halo(HL.shop, s.x, s.y, 0.75);
  for (const B of BLOCKS) if (B.dome) halo(HL.dome, B.dome.x, B.dome.top + 3, 0.7 + 0.15 * wave(t, 20, B.dome.x * 0.01));
}
function drawObelisco(t) {
  halo(HL.obGlow, OB.x, 110, 1);
  // the floodlights' pool on the paving round its foot, under the stone so the plinth keeps its shading
  halo(HL.flood, OB.x - 6, OB.base, 0.9); halo(HL.flood, OB.x + 6, OB.base, 0.7);
  put1(OB_C[0]);
  ctx.globalAlpha = 0.5 + 0.5 * wave(t, 2, 0.25); put1(OB_C[1]);              // the floodlights swing slowly round
  const flag = env(t, 112, 160, 6);
  if (flag > 0) { ctx.globalAlpha = flag; put1(OB_C[2]); }
  ctx.globalAlpha = 1;
  // a soft band of light creeping up the shaft, once every minute
  const u = frac(t / 60), by = Math.round(lerp(OB.base - 4, OB.top, u));
  for (let y = by - 6; y <= by + 6; y++) {
    if (y < OB.top || y > OB.base - 3) continue;
    const hw = obHW(y), a = 0.12 * (1 - Math.abs(y - by) / 7) * Math.sin(Math.PI * u);
    ctx.globalAlpha = a; ctx.fillStyle = flag > 0.5 ? '#c8e4ff' : '#fffaf0'; ctx.fillRect(Math.round(OB.x - hw), y, Math.round(hw * 2) + 1, 1);
  }
  ctx.globalAlpha = 1;
  const pulse = 0.5 + 0.5 * Math.sin(TAU * 120 * t / LOOP);                                     // the aircraft lamp, every 2 s
  ctx.globalAlpha = 0.4 + 0.6 * pulse; px(ctx, OB.x, OB.tip - 1, '#ff3a2a'); ctx.globalAlpha = 1;
  halo(HL.red, OB.x, OB.tip - 1, 0.25 + 0.75 * pulse);
}

/* ================= The wet avenue: mirror, lamp streaks, light on the sidewalk ================= */
const STREAKS = [
  ...AVE_LAMPS.flatMap((L, i) => [{ x: L.x - 8, w: 3, col: '#ffe0a0', a: 0.4, seed: i * 2 }, { x: L.x + 9, w: 3, col: '#ffe0a0', a: 0.4, seed: i * 2 + 1 }]),
  { x: OB.x, w: 6, col: '#eef0ff', a: 0.22, seed: 9 }, { x: MARQ.x + 32, w: 10, col: '#ffd070', a: 0.26, seed: 10 },
  { x: CORR_VP.x, w: 5, col: '#ffe0a0', a: 0.22, seed: 11 }, { x: 70, w: 6, col: '#ffe6b8', a: 0.2, seed: 12 }, { x: 160, w: 6, col: '#c8e0ff', a: 0.18, seed: 13 },
];
function drawRoad(t, billboard) {
  put1(ROAD_C);
  reflect(ctx, t, { top: ROAD0, rows: MED0 - ROAD0, squash: 1.5, alpha: 0.55, k1: 40, k2: 96, amp: 0.9, grow: 0.08 });
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, ROAD0 + 1, MED0);
  if (billboard) streak(ctx, t, BB.x + (BB.w >> 1), 8, '#c8d8ff', 0.16, 14, ROAD0 + 1, MED0);
  drawLane(t, 0); drawLane(t, 1);
  put1(MED_C);
  reflect(ctx, t, { top: MED1 + 1, rows: ROAD1 - MED1 - 1, squash: 1, alpha: 0.3, k1: 45, k2: 110, amp: 1 });
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a * 0.6, s.seed + 20, MED1 + 2, ROAD1);
  drawLane(t, 2); drawLane(t, 3);
}
function drawWalkLight(t) {
  drawPool(POOL.kiosk, KIOSCO.x + 22, KIOSCO.y + 5, 0.9);
  drawPool(POOL.lamp, FAROLA.x, FAROLA.base - 2, 0.88);
  drawPool(POOL.pizza, 462, PIZZA.y + 2, 0.85);
  drawPool(POOL.small, MARQ.x + 32, ROAD1 + 4, 0.3);
  // the sidewalk is still wet: every light lays a broken streak on the tiles
  for (const s of WALK_STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.y0, H);
}
const WALK_STREAKS = [
  { x: FAROLA.x, w: 5, col: '#ffdca0', a: 0.3, seed: 31, y0: FAROLA.base + 1 }, { x: KIOSCO.x + 20, w: 12, col: '#ffe6b8', a: 0.18, seed: 32, y0: KIOSCO.y + 2 },
  { x: 466, w: 10, col: '#ffd890', a: 0.2, seed: 33, y0: PIZZA.y + 1 }, { x: OB.x, w: 4, col: '#e8ecff', a: 0.12, seed: 34, y0: WALK0 + 2 },
  { x: MARQ.x + 32, w: 8, col: '#ffd070', a: 0.14, seed: 35, y0: WALK0 + 2 }, { x: 74 - 8, w: 3, col: '#ffe0a0', a: 0.14, seed: 36, y0: WALK0 + 2 },
  { x: 190 + 9, w: 3, col: '#ffe0a0', a: 0.14, seed: 37, y0: WALK0 + 2 },
];

/* ================= Pre-render everything static ================= */
let HL, POOL, SKY_C, MOON_C, CLOUDS, FAR_C, CITY_C, OB_C, PLAZA_C, ROAD_C, MED_C, WALK_C, AVE_C, FAROLA_C, TRUNK_C, CANOPY_C,
  VIG_C, BB_C, AD_C, PITCH, PUFF_S, ACTORS;
function prerender() {
  HL = {
    moon: makeHalo(28, '#dfe4f0', 0.16, 1.5), landing: makeHalo(8, '#fff4d0', 0.5, 1.4), red: makeHalo(5, '#ff4a3a', 0.6, 1.3),
    obGlow: makeHalo(70, '#dde4ff', 0.11, 1.5), flood: makeHalo(18, '#f4f0ff', 0.32, 1.4), head: makeHalo(8, '#fff0c8', 0.45, 1.4),
    tail: makeHalo(4, '#ff3a3a', 0.45, 1.3), busWin: makeHalo(18, '#ffe2a0', 0.14, 1.5), phone: makeHalo(4, '#cfe0ff', 0.45, 1.2),
    flash: makeHalo(12, '#ffffff', 0.8, 1.2), candle: makeHalo(5, '#ffc060', 0.45, 1.3), oven: makeHalo(16, '#ff8a3a', 0.3, 1.4),
    kiosk: makeHalo(30, '#ffe6b8', 0.18, 1.5), marq: makeHalo(40, '#ffd070', 0.2, 1.5), blade: makeHalo(18, '#ff6a5a', 0.2, 1.4),
    corr: makeHalo(26, '#ffd890', 0.22, 1.5), shop: makeHalo(12, '#ffc878', 0.18, 1.4), dome: makeHalo(6, '#ffd890', 0.4, 1.4),
    lamp: makeHalo(30, '#ffd49a', 0.18, 1.6), lampCore: makeHalo(7, '#fff4dc', 0.5, 1.3), aveLamp: makeHalo(20, '#ffe0a8', 0.2, 1.5),
    bbLamp: makeHalo(6, '#ffe8c0', 0.5, 1.5), bbWash: makeHalo(30, '#ffe0b0', 0.1, 1.5), beacon: makeHalo(4, '#ff3a3a', 0.5, 1.3),
  };
  POOL = { kiosk: makePool(30, 6, '#ffd890', 0.22), lamp: makePool(40, 9, '#ffcc88', 0.22), pizza: makePool(26, 6, '#ffc070', 0.24), small: makePool(30, 3, '#ffd070', 0.3) };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR_C = trim(renderFar()); CITY_C = trim(renderCity()); OB_C = [0, 1, 2].map(m => trim(renderObelisco(m))); PLAZA_C = trim(renderPlaza());
  ROAD_C = trim(renderRoad());
  MED_C = trim(renderMedian());
  WALK_C = trim(renderWalk()); AVE_C = trim(renderAveLamps()); FAROLA_C = renderFarola(); TRUNK_C = trim(renderTrunk()); CANOPY_C = renderCanopy();
  KIOSK_C = trim(renderKiosco()); NEAR_C = renderNear();
  BB_C = renderBillboard(); AD_C = renderAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#74acdf' });
  PUFF_S = [null]; for (let r = 1; r <= 6; r++) PUFF_S.push(makePuff(r, '#c8bcc8', '#8a8296'));
  VIG_C = renderVignette();
  buildVehicles(); buildWalkers(); buildBando(); buildWatchers(); buildMate(); buildTables(); buildPizza(); buildKiosco(); buildDogs();
  // everyone and everything standing on our sidewalk, sorted by the row of their feet (far to near); rank 0 = always
  // drawn (things, and those who look at the crowd level themselves), CAST = at every level but 0
  ACTORS = [];
  const A = (y, draw, rank = 0) => ACTORS.push({ y, draw, rank });
  WALKERS.forEach((w, i) => A(w.y, (t, fr) => drawWalker(w, i, t, fr), w.rank));
  WATCHERS.forEach((Wt, i) => A(Wt[1], t => drawWatcher(Wt, i, t), Wt.rank));
  TABLES.forEach((T, k) => A(T.y, (t, fr, crowd) => drawTable(T, k, t, crowd)));
  A(KIOSCO.y, drawKiosco); A(240, drawKioskCustomer, CAST); A(BANDO.y, (t, fr, crowd) => drawBando(t, crowd)); A(TANGO.y, drawCouple, CAST);
  A(BENCH.y, (t, fr, crowd) => drawBench(t, crowd)); A(WAITER.y, drawWaiter, CAST); A(DOGWALK.y, drawDogWalk, CAST);
  A(NEAR_TREE.base, () => put1(TRUNK_C));
  A(FAROLA.base, t => { ctx.drawImage(FAROLA_C, FAROLA.x - 5, FAROLA.base - 65); const f = 0.94; halo(HL.lamp, FAROLA.x, FAROLA.base - 59, f); halo(HL.lampCore, FAROLA.x, FAROLA.base - 59, f); });
  A(PIZZA.y, (t, fr, crowd) => { ctx.drawImage(NEAR_C, NEAR_X, 0); text(ctx, 'PIZZA', NEAR_X + 5, 202, '#c8202e'); drawNearFx(t, crowd); });
  A(PIZZA.y, drawPizza);
  ACTORS.sort((a, b) => a.y - b.y);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the site's face on the sign; billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick
// (site/js/engine/weather.js), the city's own blossoms when left out; crowd: the share of the people, 0..1 (CROWD when
// left out or not a number)
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, fr = step(t, 768);
  // the sky: stars, the young moon, two decks of cloud, a plane, now and then a shooting star
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e4e8ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  if (!reducedMotion) drawShootingStar(t);
  halo(HL.moon, MOON.x, MOON.y, 0.9);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  drawPlane(t);
  // the towers beyond, far blossoms, the far side of the avenue
  put1(FAR_C);
  for (const f of FAR_LIGHTS) if (wave(t, f.k, f.ph) > 0.3) px(ctx, f.x, f.y, '#ffd890');
  BEACONS.forEach((b, i) => { const on = frac(t / 2 + b.ph) < 0.3; if (on) { px(ctx, b.x, b.y, '#ff3a3a'); halo(HL.beacon, b.x, b.y, 0.8); } });
  if (wx.blossoms) drawBlossoms(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  put1(CITY_C);
  drawCityFx(t, crowd);
  drawNeighbour(t, crowd);
  drawFarFolk(t, fr, crowd);
  if (billboard) drawBillboard(t, pitch);
  // the plaza, then the Obelisco standing on the paved middle of its oval (its plinth's foot is the oval's centre
  // row), then the people on the lawn
  put1(PLAZA_C);
  drawObelisco(t);
  drawPlazaFolk(t, fr, crowd);
  put1(AVE_C);
  for (const L of AVE_LAMPS) for (const s of [-1, 1]) halo(HL.aveLamp, L.x + (s > 0 ? 1 : 0) + s * 8, L.y, 0.92);
  // the wet avenue and its traffic, then our sidewalk and everyone on it
  drawRoad(t, billboard);
  put1(WALK_C);
  // the wet tiles mirror the avenue, the Obelisco and the lit city faintly
  reflect(ctx, t, { top: WALK0 + 1, rows: H - WALK0 - 1, squash: 2.1, alpha: 0.2, k1: 30, k2: 80, amp: 0.7, grow: 0.03 });
  drawWalkLight(t);
  for (const a of ACTORS) if (seen(a.rank, crowd)) a.draw(t, fr, crowd);
  puffs(t);
  if (wx.blossoms) drawBlossoms(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the jacaranda's canopy overhead, the nearest blossoms
  drawCanopy(t);
  if (wx.blossoms) drawBlossoms(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}
// smoke off the parrilla's grill, out of a vent in the near building
const SMOKE = [];
{ const r = rng(62); for (let i = 0; i < 12; i++) SMOKE.push({ n: 28 + Math.floor(r() * 8), ph: r(), k: 20 + Math.floor(r() * 20), p: r(), s: 0.7 + r() * 0.6 }); }
function puffs(t) {
  for (const s of SMOKE) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = NEAR_X + 2 - (0.35 * u + 0.65 * u * u) * 34 * s.s + wave(t, s.k, s.p) * 2 * u, y = 190 - u * 70 + u * u * 16;
    const rr = Math.max(1, Math.min(6, 1 + Math.floor(u * 7)));
    ctx.globalAlpha = 0.32 * Math.min(1, u * 14) * Math.pow(1 - u, 1.2);
    ctx.drawImage(PUFF_S[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}

export default {
  id: 'buenos-aires',
  name: 'Buenos Aires',
  tagline: 'Jacarandas by the Obelisco',
  timeZone: 'America/Argentina/Buenos_Aires',
  accent: '#74acdf',                                   // Argentine sky blue, written out: tools/seo/seo.mjs reads it from the source
  focusX: OB.x,
  landmark: [OB.x - 11, OB.x + 11],                    // the Obelisco with its plinth
  landmarkTop: OB.tip - 2,                             // the aircraft lamp on its tip
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) takes 0..1
  ambience: { traffic: 0.45, crowd: 0.2, wind: 0.1 },
  events: [
    ...BUS_RUNS.filter(b => b.lane >= 2).map(b => ({ t: b.a + 1, type: 'horn', pan: [-0.8, 0.8], dur: b.b - b.a, gain: 0.12 })),
    ...DIPS.map(t => ({ t: t + 1, type: 'chime', x: TANGO.x, gain: 0.18 })),
    { t: 114, type: 'chime', x: OB.x, gain: 0.25 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
